import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Camera,
  CheckCircle2,
  Circle,
  MessageSquare,
  Trash2,
  Navigation,
  Phone,
  MessageCircle,
  Lock,
  RotateCcw,
  Send,
  Image as ImageIcon,
  AlertTriangle,
  Clock,
  Wrench,
  Receipt,
  Pencil,
  Save,
  Play,
  Pause,
  // 2026-02-18 Bug #7 — task reorder + per-task photo
  ArrowUp,
  ArrowDown,
  // Iter 427.p38 — drag-and-drop task reorder
  GripVertical,
  Camera as CameraIcon,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import api from "../lib/api";
import { EntityShell } from "../components/shell";
import HistoryBlock from "../components/shell/HistoryBlock";
import ShareButton from "../components/ShareButton";
import UploadScanBadge from "../components/uploads/UploadScanBadge";
import { useAutoRefresh } from "../hooks/useAutoRefresh";
import { phoneToWa } from "../lib/contact";
import PhaseChip from "../components/PhaseChip";
import SignaturePad from "../components/SignaturePad";
import DateTimePicker from "../components/DateTimePicker";
import SearchableSelect from "../components/SearchableSelect";
import MediaLightbox from "../components/MediaLightbox";
import ProjectLinkChip from "../components/shared/ProjectLinkChip";
// Removed: import BeforeAfterBlock — see "Before/After block REMOVED" note below.
import { JCProgressBar, TaskStatusSelector } from "../components/JCProgress";
import { JCDocumentsBlock } from "../components/JCDocumentsBlock";
import VoiceTextarea from "../components/VoiceTextarea";
import JobCardMaterialsBlock from "../components/JobCardMaterialsBlock";
import EntityLinksPanel from "../components/EntityLinksPanel";
import BlurTextarea from "../components/BlurTextarea";
import { useAuth } from "../context/AuthContext";
import { JOBCARD_PHASES_EDIT, REQUIRED_SKILLS } from "../lib/enums";
import { pushRecent } from "../lib/recents";
import { AddToCalendarButton } from "../lib/calendarExport";
import EntityChatPanel from "../components/chat/EntityChatPanel";
import FromChatPill from "../components/chat/FromChatPill";
import { toast } from "sonner";
import SitePinPrompt from "../components/SitePinPrompt";
import { uploadUrl } from "../lib/uploads";
import { enqueue as enqueueUpload } from "../lib/uploadManager";
import useUploads from "../hooks/useUploads";
import ReadAloudButton from "../components/ReadAloudButton";
import ExpandableText from "../components/ExpandableText";
// Wave 25.16 Phase 2 refactor — leaf helpers extracted from this file.
import { NumField, TextField, formatHoursAsHHmm } from "../components/jobcard/JCFieldHelpers";
import RelatedTicketField from "../components/jobcard/RelatedTicketField";
import MultiLinkChipRow from "../components/MultiLinkChipRow";
import TeamMembersPicker from "../components/jobcard/TeamMembersPicker";
import TaskAssigneePicker, { taskInMyList } from "../components/jobcard/TaskAssigneePicker";
import VehiclePicker from "../components/jobcard/VehiclePicker";
import ServiceRatingBlock from "../components/jobcard/ServiceRatingBlock";
import RelatedSalesOrderField from "../components/jobcard/RelatedSalesOrderField";
import AddTechNoteButton from "../components/jobcard/AddTechNoteButton";
import useServerPdf from "../lib/useServerPdf";
import { FileDown } from "lucide-react";
import PhotoPickerButton from "../components/PhotoPickerButton";
import DictTextarea from "../components/DictTextarea";

// Build runtime phase list — exposes legacy phase as an option only if the JC is currently on it.
// Iter 418.f (Dirk, 2026-02-28) — For technicians, filter to just the
// transitions the backend actually allows (see _TECH_ALLOWED_PHASE_TRANSITIONS
// in /app/backend/routes/jobcards.py). Otherwise the tech could pick e.g.
// `signed_off` from the dropdown, get a silent 400, and see the phase
// "jump back" to in_progress once the 45-second auto-refresh runs.
const TECH_ALLOWED_TARGETS_FROM = {
  created: new Set(["in_progress"]),
  scheduled: new Set(["in_progress"]),
  ready: new Set(["in_progress"]),
  paused: new Set(["in_progress", "on_site_complete"]),
  in_progress: new Set(["paused", "on_site_complete"]),
};
const phaseOptionsFor = (currentPhase, isTech = false) => {
  let list = [...JOBCARD_PHASES_EDIT];
  if (isTech) {
    const allowed = TECH_ALLOWED_TARGETS_FROM[currentPhase] || new Set();
    list = list.filter((p) => p.key === currentPhase || allowed.has(p.key));
  }
  if (currentPhase && !list.find((p) => p.key === currentPhase)) {
    list.unshift({ key: currentPhase, label: `${currentPhase.replaceAll("_", " ")} (current)` });
  }
  return list;
};

const PHASES = JOBCARD_PHASES_EDIT;

export default function JobCardDetail() {
  const { id } = useParams();
  const isCreate = id === "new";
  const { user } = useAuth();
  const isManager = user && (user.role === "owner" || user.role === "admin" || user.role === "manager");
  const [job, setJob] = useState(isCreate ? {
    // Empty template for the create flow — same editor, just an unsaved
    // shape. DESIGN_CONTRACT §1: there is exactly one editor per entity,
    // create is the same editor with an empty template.
    id: null,
    title: "",
    description: "",
    customer_id: "",
    site_id: "",
    phase: "scheduled",
    technician_id: null,
    scheduled_at: null,
    scheduled_end: null,
    items: [],
    photos: [],
  } : null);
  const [customer, setCustomer] = useState(null);
  const [site, setSite] = useState(null);
  const [allSitesForCustomer, setAllSitesForCustomer] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [newItem, setNewItem] = useState("");
  const itemFileRef = useRef({});
  const [commentDrafts, setCommentDrafts] = useState({});
  // Iter 398j — Dirk showed screenshots of the same comment landing
  // 2×/3×/4× (one second apart). Root cause: `addItemComment` had no
  // in-flight guard, so a double-tap on the Add button or a double
  // Ctrl+Enter fired two parallel POSTs that both succeeded. Now we
  // track the busy-per-task in a Set — the second call is a no-op
  // until the first resolves.
  const [commentBusy, setCommentBusy] = useState({});
  // Wave 26.10.8 — per-task edit mode. Only ONE task can be in edit mode
  // at a time. View mode (default) shows static label + comments + status
  // pills. Edit mode unlocks the comment composer (and for managers, the
  // title textarea). Locking edits behind this prevents accidental edits
  // when scrolling on mobile.
  const [editingTaskId, setEditingTaskId] = useState(null);
  // [Iter 349 — 2026-02-26] "My tasks" filter for the checklist.
  // When on, only tasks assigned to the current user OR fully open
  // (no assignees) are shown. Persists per-JC in sessionStorage so a
  // tech's filter choice survives a page refresh but doesn't leak
  // between different JCs.
  const [myTasksOnly, setMyTasksOnly] = useState(() => {
    try {
      return sessionStorage.getItem(`jc-my-tasks-${id}`) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(`jc-my-tasks-${id}`, myTasksOnly ? "1" : "0");
    } catch { /* sessionStorage may be blocked in private mode */ }
  }, [myTasksOnly, id]);
  // Iter 418.d (Dirk, 2026-02-28) — Status filter for the To-do list.
  // Options: "all" · "todo" (pending/unset) · "in_progress" · "done".
  // Persisted per-JC in sessionStorage so it survives a refresh.
  // Mobile-first: rendered below as a native <select> so the OS
  // picker fires (no custom overlay to fight with on a small screen).
  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      return sessionStorage.getItem(`jc-status-filter-${id}`) || "all";
    } catch {
      return "all";
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(`jc-status-filter-${id}`, statusFilter);
    } catch { /* sessionStorage may be blocked */ }
  }, [statusFilter, id]);
  // Local DRAFT state for task (checklist) labels. We keep an in-memory copy
  // per task so typing is buttery smooth — the server PATCH is debounced
  // (300 ms) and runs AFTER the last keystroke, which fixes the race where
  // a slow server response would overwrite the characters the user is
  // actively typing. Mapping: { [task_index]: string }.
  const [labelDrafts, setLabelDrafts] = useState({});
  const labelSaveTimers = useRef({});
  // Same pattern as labelDrafts but for the JOB-LEVEL free-text fields
  // (title, description, special_instructions, invoice_notes). Without this
  // every keystroke fires PUT + load(), and the load() response overwrites
  // the in-progress text — destroying characters when typing fast.
  // Tracked-pending patch is debounced & flushed 500 ms after the last edit.
  const fieldSaveTimerRef = useRef(null);
  const pendingFieldPatchRef = useRef({});
  // Wave 29 Chunk 8c — lightbox holds {urls[], index, label?} so the new
  // MediaLightbox can drive prev/next within the group of photos that was
  // opened (job photos vs per-checklist-item photos vs notes attachments).
  const [lightbox, setLightbox] = useState(null);
  const [createErr, setCreateErr] = useState("");

  const load = async () => {
    if (isCreate) {
      // Create-mode: don't fetch the job, but DO fetch the customer
      // catalogue so the picker has options. Sites are scoped per-customer
      // and are fetched lazily when the user picks one.
      try {
        const [cs, us] = await Promise.all([
          api.get("/customers"),
          api.get("/users"),
        ]);
        setAllCustomers(cs.data || []);
        setUsers(us.data || []);
      } catch (_e) { /* swallow — UI handles empty state */ }
      return;
    }
    const { data } = await api.get(`/jobcards/${id}`);
    setJob(data);
    if (data) {
      pushRecent({
        kind: "jobcard",
        id: data.id,
        label: data.title || data.short_id || data.id?.slice(0, 8),
        sub: data.short_id || "",
      });
    }
    if (data.customer_id) {
      const c = await api.get(`/customers/${data.customer_id}`);
      setCustomer(c.data);
    }
    if (data.site_id) {
      const allSites = await api.get(`/sites?customer_id=${data.customer_id}`);
      setAllSitesForCustomer(allSites.data || []);
      setSite((allSites.data || []).find((s) => s.id === data.site_id));
    } else if (data.customer_id) {
      const allSites = await api.get(`/sites?customer_id=${data.customer_id}`);
      setAllSitesForCustomer(allSites.data || []);
      setSite(null);
    } else setSite(null);
    const u = await api.get("/users");
    setUsers(u.data);
    // Customer catalogue is only needed when the user toggles edit mode and
    // wants to repoint the job card. Lazy-fetch on first edit.
  };

  useEffect(() => {
    load();
  }, [id]);

  // Wave 19 #84 — mark every alert about this JC as read when the detail
  // page mounts so the rose pulse on the list row clears as soon as the
  // user opens it. Best-effort, fire-and-forget.
  useEffect(() => {
    if (!id || id === "new") return;
    // Iter 364 (2026-02-27) — Dirk: viewing the JC must clear its
    // notification badge immediately. Dispatch the mutated event
    // right after so the bell counter refreshes without waiting for
    // the next poll (up to 8 s).
    api.post("/notifications/mark-entity-read", {
      entity_type: "jobcard",
      entity_id: id,
    })
      .then(() => {
        try { window.dispatchEvent(new CustomEvent("hv:notifications-mutated")); }
        catch { /* old browsers */ }
      })
      .catch(() => {});
  }, [id]);

  // Flush any pending debounced text-field save when the editor unmounts
  // (user navigates away mid-typing) so the trailing keystrokes are not lost.
  useEffect(() => {
    return () => {
      if (fieldSaveTimerRef.current) clearTimeout(fieldSaveTimerRef.current);
      const flush = pendingFieldPatchRef.current;
      if (id && id !== "new" && flush && Object.keys(flush).length > 0) {
        // Fire-and-forget — component is unmounting, no need to await.
        api.put(`/jobcards/${id}`, flush).catch(() => {});
      }
      pendingFieldPatchRef.current = {};
    };
  }, [id]);

  // editMode declared early so the auto-refresh hook below can read it
  // without crashing the component (Temporal Dead Zone fix — was a
  // preexisting hook-ordering bug).
  const [editMode, setEditMode] = useState(isCreate);

  // Auto-refresh while viewing in read-only mode (Wave 19 — concurrent-edit
  // safety). Disabled in create mode (no saved record yet) AND in edit mode
  // (would clobber in-progress text fields). Slightly longer interval (45 s)
  // than list pages because detail pages are heavier.
  useAutoRefresh(load, { intervalMs: 45_000, enabled: !isCreate && !editMode });

  // manager-only edits
  const update = async (patch) => {
    // In create mode, mutate the local in-memory `job` only — there's no
    // saved record yet; the customer/site/title etc. all flow into the
    // POST when the user clicks Save.
    if (isCreate) {
      setJob((prev) => {
        const merged = { ...(prev || {}), ...patch };
        // Auto-derive Time allocation when both ends are present.
        try {
          if (merged.scheduled_at && merged.scheduled_end) {
            const ms = new Date(merged.scheduled_end) - new Date(merged.scheduled_at);
            if (ms > 0) merged.time_allocation_hours = Math.round((ms / 36e5) * 100) / 100;
          }
        } catch (_) { /* noop */ }
        return merged;
      });
      // When the customer changes, refresh the in-memory customer record
      // so the header copy + site picker scope update without a re-load.
      if ("customer_id" in patch) {
        const cid = patch.customer_id;
        const cust = (allCustomers || []).find((c) => c.id === cid) || null;
        setCustomer(cust);
        setSite(null);
        if (cid) {
          try {
            const sres = await api.get(`/sites?customer_id=${cid}`);
            setAllSitesForCustomer(sres.data || []);
            if ((sres.data || []).length === 1) {
              const only = sres.data[0];
              setJob((prev) => ({ ...(prev || {}), site_id: only.id }));
              setSite(only);
            } else {
              setJob((prev) => ({ ...(prev || {}), site_id: "" }));
            }
          } catch (_e) {
            setAllSitesForCustomer([]);
          }
        } else {
          setAllSitesForCustomer([]);
          setJob((prev) => ({ ...(prev || {}), site_id: "" }));
        }
      }
      if ("site_id" in patch && patch.site_id) {
        const s = (allSitesForCustomer || []).find((x) => x.id === patch.site_id);
        if (s) setSite(s);
      }
      return;
    }
    // Auto-derive Time allocation from start/end if they were just edited
    // and the user hasn't already set a manual override (Wave 3 rec).
    const next = { ...job, ...patch };
    if (
      ("scheduled_at" in patch || "scheduled_end" in patch) &&
      next.scheduled_at &&
      next.scheduled_end
    ) {
      try {
        const ms = new Date(next.scheduled_end) - new Date(next.scheduled_at);
        if (ms > 0) {
          patch.time_allocation_hours = Math.round((ms / 36e5) * 100) / 100;
        }
      } catch (_) {
        /* noop */
      }
    }
    // When the customer is repointed on an existing JC, reset the site
    // (the old site no longer belongs to the new customer).
    if ("customer_id" in patch && patch.customer_id !== job?.customer_id) {
      patch.site_id = null;
    }
    // OPTIMISTIC LOCAL UPDATE — keeps the input value in lock-step with the
    // user's keystrokes. Without this, fast typing in <input>s lost
    // characters because the next render's value came from the (slow) server
    // response via load(). Wave 18 #77.
    setJob((prev) => ({ ...(prev || {}), ...patch }));

    // Decide whether to debounce. Free-text fields (title, description,
    // special_instructions, invoice_notes) are debounced 500 ms so we send
    // ONE PUT after typing stops — and we DON'T call load() afterwards,
    // because that's the call that used to clobber in-progress text.
    // Everything else (phase, technician, dates, checklist, customer/site)
    // saves immediately the way it always did — those are dropdown picks
    // where the user pauses naturally between actions.
    const TEXT_FIELDS = new Set(["title", "description", "special_instructions", "invoice_notes"]);
    const isTextOnly = Object.keys(patch).length > 0
      && Object.keys(patch).every((k) => TEXT_FIELDS.has(k));

    if (isTextOnly) {
      pendingFieldPatchRef.current = { ...pendingFieldPatchRef.current, ...patch };
      if (fieldSaveTimerRef.current) clearTimeout(fieldSaveTimerRef.current);
      fieldSaveTimerRef.current = setTimeout(async () => {
        const flush = pendingFieldPatchRef.current;
        pendingFieldPatchRef.current = {};
        fieldSaveTimerRef.current = null;
        if (Object.keys(flush).length === 0) return;
        try {
          await api.put(`/jobcards/${id}`, flush);
        } catch (_e) {
          // Surface in console; fields stay in local state so user can retry.
          // Avoid noisy toast — they're typing, not committing.
          // eslint-disable-next-line no-console
          console.warn("Job text autosave failed", _e);
        }
        // INTENTIONALLY no load() here — see comment above.
      }, 500);
      return;
    }

    // Non-text patch: save now and refresh, like before.
    // Iter 418.f (Dirk, 2026-02-28) — Add try/catch + revert so a
    // rejected PUT (e.g. tech tries to advance to on_site_complete
    // via the phase dropdown without capturing sig/rating first)
    // doesn't leave the UI in a lying optimistic state that then
    // "jumps back" 45 s later when useAutoRefresh runs. Now the
    // failure surfaces immediately with a toast + inline revert.
    const _prevSnapshot = job ? { ...job } : null;
    try {
      await api.put(`/jobcards/${id}`, patch);
      load();
    } catch (e) {
      // Revert the optimistic state so what the user sees == what
      // the server has.
      if (_prevSnapshot) {
        setJob((prev) => {
          const restored = { ...(prev || {}) };
          Object.keys(patch).forEach((k) => { restored[k] = _prevSnapshot[k]; });
          return restored;
        });
      }
      const detail = e?.response?.data?.detail || e?.message || "Update failed.";
      toast.error(detail);
      // Also refresh from server so any parallel changes come through.
      try { load(); } catch (_) { /* noop */ }
    }
  };

  // Edit lock — manager view starts read-only; click Edit to unlock metadata fields.
  // Technicians never need the lock (their allowed surface is always interactive).
  // Create mode always starts in edit (the whole template is unsaved).
  // Note: `editMode` itself is declared earlier (above `useAutoRefresh`)
  // to avoid a TDZ crash; only the companion state lives here.
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Phase 7 Sub-iter 7A Stage B — tab navigation across the JC detail.
  const [tab, setTab] = useState("overview");
  const editLocked = isManager && !editMode;
  const navigate = useNavigate();
  // Iter 418.t (Dirk, 2026-02-28) — CRM-side download of the customer-
  // portal PDF variant. Same endpoint the portal itself hits, so what
  // the user downloads here is byte-identical to what the customer
  // sees. `as_client=1` flag tells the renderer to strip internal-only
  // fields (cost, wet notes, staff pay).
  const jcShortId = job?.short_id || job?.id?.slice(0, 8) || "jobcard";
  const { download: downloadJcPdf, busy: downloadingJcPdf } = useServerPdf({
    endpoint: !isCreate && id ? `/jobcards/${id}/pdf?as_client=1` : null,
    filename: `JobCard_${jcShortId}.pdf`,
  });

  // Lazy-fetch the full customer catalogue the first time the user enters
  // edit mode on an existing JC — for create mode load() already did it.
  useEffect(() => {
    if (!editMode || isCreate) return;
    if ((allCustomers || []).length > 0) return;
    (async () => {
      try {
        const cs = await api.get("/customers");
        setAllCustomers(cs.data || []);
      } catch (_e) { /* swallow */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  // Flush any pending debounced text-field PUT immediately. Used by
  // handleSave / handleCancel / unmount so the last few keystrokes never
  // get lost when the user commits before the 500 ms debounce fires.
  const flushPendingTextPatch = async () => {
    if (fieldSaveTimerRef.current) {
      clearTimeout(fieldSaveTimerRef.current);
      fieldSaveTimerRef.current = null;
    }
    const flush = pendingFieldPatchRef.current;
    pendingFieldPatchRef.current = {};
    if (id && id !== "new" && Object.keys(flush).length > 0) {
      try { await api.put(`/jobcards/${id}`, flush); } catch (_e) { /* noop */ }
    }
  };

  const handleSave = async () => {
    if (isCreate) {
      // Backend invariants enforce customer_id + site_id + a non-empty
      // title. Surface those errors inline rather than throwing a toast.
      if (!job?.customer_id) { setCreateErr("Pick a customer."); return; }
      if (!job?.site_id) { setCreateErr("Pick a site for that customer."); return; }
      if (!(job.title || "").trim()) { setCreateErr("Title is required."); return; }
      setCreateErr("");
      try {
        const payload = {
          customer_id: job.customer_id,
          site_id: job.site_id,
          title: (job.title || "").trim(),
          description: (job.description || "").trim() || null,
          phase: job.phase || "scheduled",
          technician_id: job.technician_id || null,
          scheduled_at: job.scheduled_at || null,
          scheduled_end: job.scheduled_end || null,
          time_allocation_hours: job.time_allocation_hours,
          checklist: job.checklist || [],
          job_card_reason: job.job_card_reason || null,
          related_ticket_id: job.related_ticket_id || null,
          related_sales_order_id: job.related_sales_order_id || null,
          special_instructions: job.special_instructions || null,
          invoice_notes: job.invoice_notes || null,
        };
        // Strip nulls so the backend's optional fields don't get clobbered.
        Object.keys(payload).forEach((k) => {
          if (payload[k] === null || payload[k] === undefined) delete payload[k];
        });
        const { data } = await api.post(`/jobcards`, payload);
        // Land on the live record — same editor, now bound to the saved id.
        navigate(`/jobcards/${data.id}`, { replace: true });
      } catch (e) {
        const status = e?.response?.status;
        const d = e?.response?.data?.detail;
        let msg = typeof d === "string"
          ? d
          : Array.isArray(d) ? d.map((x) => x?.msg || JSON.stringify(x)).join("; ")
          : "Could not create job card.";
        // Friendlier wording for the common HTTP errors so reps/techs
        // understand why their save bounced.
        if (status === 403 || /^forbidden$/i.test(msg)) {
          msg = "Only Admin, Manager or Owner can create job cards. Ask one of them to create it, or convert from a lead/ticket.";
        } else if (status === 401) {
          msg = "Your session has expired. Please sign in again.";
        }
        setCreateErr(msg);
      }
      return;
    }
    setEditMode(false);
    // Field-level updates have already auto-saved; this exits the editor.
    // Flush any text-field debounce that's still in flight so the trailing
    // keystrokes aren't lost.
    flushPendingTextPatch();
  };
  // Manager edit-mode gating for the top metadata fields.
  // When editMode is OFF, manager sees read-only metadata. Techs see read-only.
  const canEditMeta = isManager && editMode;
  // Tech "Start mode" — analogous to manager's editMode but for technicians.
  // The JC is fully read-only by default for techs. Pressing Start unlocks
  // the tech-allowed surface (checklist, photos, materials, sign-off,
  // ratings) AND moves the phase to `in_progress`. Pressing Pause moves
  // it to `paused` (still unlocked so they can resume). Pressing Complete
  // moves it to `on_site_complete`, locks tech UI, and notifies managers.
  // Persisted server-side via `phase` so it survives logout/login.
  const techRoleAllowed = user?.role === "technician" || isManager;
  const isAssignedTech = user?.role === "technician" && (
    job?.assigned_user_id === user?.id ||
    (Array.isArray(job?.assigned_user_ids) && job.assigned_user_ids.includes(user.id))
  );
  // While the JC's phase is in_progress or paused, the tech surface is
  // unlocked. on_site_complete locks again — only manager can revert.
  const techStartMode = !isCreate && (
    job?.phase === "in_progress" || job?.phase === "paused" || job?.phase === "complete"
  );
  // Wave 19 #82 — strict on-site-complete contract. Tech can only press
  // "Complete" once we have ALL of: customer signature, customer rating,
  // technician self-rating, AND technician signature. Backend enforces
  // the same rule (`_assert_on_site_complete_ready`).
  const _missingForComplete = (() => {
    const missing = [];
    if (!(job?.client_signoff && job.client_signoff.signed_at)) missing.push("customer signature");
    if (!job?.rating_customer) missing.push("customer rating");
    if (!job?.rating_technician) missing.push("technician self-rating");
    if (!(job?.tech_signoff && job.tech_signoff.signed_at)) missing.push("technician signature");
    return missing;
  })();
  const techCanComplete = _missingForComplete.length === 0;
  // Wave 26.8 — owner / admin / manager always have full task-action
  // parity with technicians (phase select, status quick-flip, comments,
  // photos, sign-off). The major-edit pencil only gates META fields
  // (customer, site, technician assignment, billing). Tech surface is
  // unlocked when the JC is in_progress / paused / complete; a manager
  // is treated as always-unlocked for the tech surface (so they can
  // stand in for an absent technician on the spot).
  const canEditPhase = isManager || (techRoleAllowed && techStartMode);
  // ── Workflow buttons (Start / Pause / Complete) ─────────────────────
  // Save the new phase IMMEDIATELY (no debounce) so the workflow state
  // survives across logout/login on any device. Recommendation #3a.
  const transitionPhase = async (next) => {
    if (!job?.id) return;
    try {
      // Wave 29 Phase F.1 — geo-tag JC start + complete events. The
      // /jobcards/{id}/phase endpoint is the lightweight transition
      // route used by techs; it doesn't accept a location_event body,
      // so we route start / on_site_complete through the full PUT
      // endpoint to attach the captured GPS pin. All other
      // transitions still use the lightweight endpoint.
      const wantsLocation = next === "in_progress" || next === "on_site_complete";
      if (wantsLocation) {
        let loc = null;
        try {
          const { requestPosition } = await import("../lib/geo");
          loc = await requestPosition({ timeoutMs: 10000 });
        } catch (_e) { loc = null; }
        await api.put(`/jobcards/${job.id}`, {
          phase: next,
          ...(loc ? { location_event: loc } : {}),
        });
      } else {
        await api.patch(`/jobcards/${job.id}/phase`, { phase: next });
      }
      // Refresh the JC so the new start_location / complete_location +
      // audit flag are visible on the banner.
      try {
        const fresh = await api.get(`/jobcards/${job.id}`);
        setJob(fresh.data);
      } catch (_e) {
        setJob((j) => ({ ...j, phase: next }));
      }
      // Backend fans out manager/admin/owner notifications when
      // phase==='on_site_complete' (Wave 19 #82). No client-side call
      // needed.
    } catch (e) {
      // Iter 418.f — surface via toast (not alert) so the message
      // is readable on mobile, and force a fresh load so any stale
      // optimistic state gets reverted immediately.
      const detail = e?.response?.data?.detail || e?.message || "Failed to update job card status.";
      toast.error(detail);
      try { const fresh = await api.get(`/jobcards/${job.id}`); setJob(fresh.data); } catch (_) { /* noop */ }
    }
  };
  const canStart = !isCreate && techRoleAllowed && (
    job?.phase === "scheduled" || job?.phase === "ready" || job?.phase === "paused" || job?.phase === "created"
  );
  const canPause = !isCreate && techRoleAllowed && job?.phase === "in_progress";
  const canResume = !isCreate && techRoleAllowed && job?.phase === "paused";
  const canFinishOnSite = !isCreate && techRoleAllowed && (
    job?.phase === "in_progress" || job?.phase === "paused" || job?.phase === "complete"
  ) && techCanComplete;
  const isOnSiteComplete = job?.phase === "on_site_complete" || job?.phase === "signed_off" ||
                            job?.phase === "ready_to_invoice" || job?.phase === "invoice_sent" ||
                            job?.phase === "closed" || job?.phase === "paid";
  const handleCancel = () => {
    if (isCreate) {
      navigate("/jobcards");
      return;
    }
    // Exit edit mode immediately; flush any pending text-field debounce
    // so we don't lose the last ~500 ms of typing, then refetch from the
    // server to catch up.
    flushPendingTextPatch().then(() => {
      setEditMode(false);
      load();
    });
  };
  const handleDelete = async () => {
    await api.delete(`/jobcards/${id}`);
    navigate("/jobcards");
  };

  const addItem = async () => {
    if (!isManager || !newItem.trim()) return;
    // Wave 25.14 — assign a CLIENT-SIDE UUID immediately so per-task state
    // (commentDrafts, labelDrafts, photo refs) keys don't collide on the
    // shared `undefined` slot when two new tasks exist before the server
    // assigns ids. Server's Pydantic ChecklistItem accepts an inbound id,
    // so this round-trips cleanly.
    const newId = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const checklist = [...(job.checklist || []), { id: newId, label: newItem, status: "pending", comments: [], photos: [] }];
    setNewItem("");
    await update({ checklist });
    // Wave 25.14 — auto-focus the newly added task's title editor so the
    // user can keep typing without a tap. Try a few times because hot-reload
    // + load() round-trip means the row may not be in the DOM immediately.
    let tries = 0;
    const focusNew = () => {
      const el = document.querySelector(`[data-testid="task-title-${newId}"] textarea, [data-testid="task-title-${newId}"]`);
      if (el && typeof el.focus === "function") {
        el.focus();
        return;
      }
      if (tries++ < 20) setTimeout(focusNew, 60);
    };
    setTimeout(focusNew, 80);
  };

  // Iter 349.h (2026-02-25) — Tech-added note follow-up.
  // Called by `AddTechNoteButton` after it successfully POSTs a new
  // tech_note item. Backend returns the full updated jobcard + the
  // new item's id. We swap it into local state (no full reload — the
  // reload race was what caused the label editor to briefly render
  // the stale placeholder text) and auto-flip the new row into edit
  // mode + focus its label textarea, mirroring `addItem` above.
  const handleTechNoteCreated = (updatedJob, newId) => {
    if (updatedJob) setJob(updatedJob);
    else if (load) load();
    if (!newId) return;
    setEditingTaskId(newId);
    // Pre-seed the label draft to an empty string so the placeholder
    // text ("Describe what you did on site…") shows immediately when
    // the textarea mounts, instead of the backend's default seed
    // ("(Tap to describe …)") flashing.
    if (updatedJob?.checklist) {
      const idx = updatedJob.checklist.findIndex((c) => c?.id === newId);
      if (idx >= 0) {
        setLabelDrafts((prev) => ({ ...prev, [idx]: "" }));
      }
    }
    let tries = 0;
    const focusNew = () => {
      const el = document.querySelector(
        `[data-testid="task-title-${newId}"] textarea, [data-testid="task-title-${newId}"]`,
      );
      if (el && typeof el.focus === "function") {
        el.focus();
        try { el.select(); } catch (_e) { /* noop */ }
        return;
      }
      if (tries++ < 25) setTimeout(focusNew, 60);
    };
    setTimeout(focusNew, 100);
  };

  // Rename a task. `immediate=true` triggers the save right now (onBlur);
  // otherwise we keep only the local draft and debounce the server PATCH by
  // 400 ms so fast typing never races with a slow PATCH response (the race
  // is what used to make characters disappear when typing fast).
  const renameItem = (idx, label, immediate = false) => {
    setLabelDrafts((prev) => ({ ...prev, [idx]: label }));

    // Cancel any pending save for this task
    if (labelSaveTimers.current[idx]) {
      clearTimeout(labelSaveTimers.current[idx]);
    }

    const doSave = async () => {
      labelSaveTimers.current[idx] = null;
      // Capture the exact value we're saving so we can safely clear the
      // draft ONLY if the user hasn't kept typing during the round-trip.
      const savedValue = label;
      const latest = (j) => (j?.checklist || []).map((it, i) => (i === idx ? { ...it, label: savedValue } : it));
      // Use the functional setter to avoid stale-state overwrites
      let checklist;
      setJob((j) => {
        checklist = latest(j);
        return j ? { ...j, checklist } : j;
      });
      if (checklist) {
        try {
          await api.put(`/jobcards/${id}`, { checklist });
        } catch (_e) { /* ignore — next keystroke will retry */ }
      }
      // 2026-02-27 (Iter 360) — Dirk: "The task typing area in JC is
      // jumpy. If you type fast it does not type." The old code
      // unconditionally deleted the draft after the PUT resolved,
      // which wiped out any keystrokes the user made while the
      // network round-trip was in-flight. Only drop the draft if the
      // current draft is still equal to what we just persisted —
      // otherwise the user is still typing and we must keep their
      // in-flight text.
      setLabelDrafts((prev) => {
        if (prev[idx] !== savedValue) return prev;
        const n = { ...prev };
        delete n[idx];
        return n;
      });
    };

    if (immediate) {
      doSave();
    } else {
      labelSaveTimers.current[idx] = setTimeout(doSave, 400);
    }
  };

  const removeItem = async (idx) => {
    const checklist = job.checklist.filter((_, i) => i !== idx);
    await update({ checklist });
  };

  // 2026-02-18 Bug #7 (Dirk) — drag-reorder UX without pulling in a
  // drag-drop lib. Up/down arrow buttons next to each task. Manager+
  // only; the assigned tech reorders by virtue of having the JC open
  // in edit mode if that's been turned on for them.
  const moveItem = async (idx, delta) => {
    const list = [...(job.checklist || [])];
    const target = idx + delta;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    await update({ checklist: list });
  };

  // Iter 427.p38 — Drag-and-drop task reorder. Dirk 2026-09-14: "Too
  // messy to click a task all the way up to position 3 from 50 —
  // drag and drop will make it a lot better." Keeps the ↑ ↓ buttons
  // (fine-grained nudges) but adds a grip handle + native HTML5 drag
  // events on each row. Splices the array on drop and persists via
  // the same `update({checklist})` path the arrow buttons use, so
  // there's exactly one write path to reason about.
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const moveTaskTo = async (fromIdx, toIdx) => {
    if (fromIdx == null || toIdx == null || fromIdx === toIdx) return;
    const list = [...(job.checklist || [])];
    if (fromIdx < 0 || fromIdx >= list.length) return;
    const clampedTo = Math.max(0, Math.min(list.length - 1, toIdx));
    const [row] = list.splice(fromIdx, 1);
    list.splice(clampedTo, 0, row);
    await update({ checklist: list });
  };

  // 2026-02-18 Bug #7 — per-task photo upload. The data model already
  // supports `task.photos[]` via PATCH `/api/jobcards/{id}/checklist/
  // {iid}` with `{add_photo_upload_id}`. We just need a UI button on
  // each row that triggers a hidden file input and pipes through the
  // same /api/uploads flow used elsewhere.
  const attachTaskPhotos = async (item, files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    let ok = 0;
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("source", "jc_task_photo");
        const up = await api.post(`/uploads`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 0,
        });
        if (up.data?.id && item.id) {
          await api.patch(`/jobcards/${id}/checklist/${item.id}`, {
            add_photo_upload_id: up.data.id,
          });
          ok += 1;
        }
      } catch (_err) {
        toast.error(`"${f.name}" — upload failed.`);
      }
    }
    if (ok) {
      toast.success(`${ok} photo${ok === 1 ? "" : "s"} attached.`);
      load();
    }
  };

  // Iter 362 (2026-02-27) — Dirk: "we can never have broken images
  // because that's the only form of proof we have." When a broken
  // photo tile is retaken, we upload the fresh file, PATCH the
  // task's checklist to insert the new upload_id, and remove the
  // broken one so the register + PDF stop showing the "?" tile.
  const replaceItemPhoto = async (taskIdx, photoIdx, newFile) => {
    const list = job?.checklist || [];
    const item = list[taskIdx];
    if (!item?.id || !newFile) return;
    try {
      const fd = new FormData();
      fd.append("file", newFile);
      fd.append("source", "jc_task_photo_retake");
      const up = await api.post(`/uploads`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
      });
      if (!up.data?.id) throw new Error("upload id missing");
      // Add the new photo, then remove the broken slot.
      await api.patch(`/jobcards/${id}/checklist/${item.id}`, {
        add_photo_upload_id: up.data.id,
      });
      const brokenValue = (item.photos || [])[photoIdx];
      if (brokenValue) {
        await api.patch(`/jobcards/${id}/checklist/${item.id}`, {
          remove_photo: brokenValue,
        });
      }
      toast.success("Photo replaced.");
      load();
    } catch (_e) {
      toast.error("Retake failed — please try again.");
    }
  };

  // tech-allowed item ops
  const toggleItem = async (item) => {
    const next = item.status === "done" ? "pending" : "done";
    await api.patch(`/jobcards/${id}/checklist/${item.id}`, { status: next });
    load();
  };

  // Wave 19 #82 — inline 3-state task button: pending → in_progress → done.
  // Saves immediately so progress survives logout/login. The user will see
  // the button colour change instantly (optimistic) then the server
  // confirms.
  // Wave 26.10.6 — added explicit error toast + auto-assign UUID for legacy
  // ID-less items (otherwise silent return = "click does nothing").
  const setTaskStatus = async (item, next) => {
    // Iter 398j — Dirk's ask: "if you hit … the done button, it must
    // only go through once." Same-state guard first — if the task is
    // already in the requested status, treat as a no-op instead of
    // firing an extra PATCH. (For status changes to `rejected` we
    // intentionally skip the busy check so the reject-reason modal
    // still opens.)
    if (item?.status === next && next !== "rejected") return;
    let workItem = item;
    if (!workItem?.id) {
      // Legacy/unsaved item — assign a stable client UUID and persist
      // before continuing so the PATCH endpoint can find it next call.
      const newId = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const checklist = (job?.checklist || []).map((it) =>
        it === item ? { ...it, id: newId } : it,
      );
      try {
        await update({ checklist });
        workItem = { ...item, id: newId };
      } catch (e) {
        toast.error("Could not save task — please refresh and try again.");
        return;
      }
    }
    // [Iter 340 — 2026-02-25] Rejected requires a reason. BUT if the
    // tech already added a comment to this task (typical flow: they
    // note "can't proceed — missing part" while it's In progress, then
    // later flip to Rejected), we accept that existing comment as the
    // reason and DON'T pester them with another modal. Only prompt when
    // the task has zero comments — otherwise just move the status.
    //
    // Dirk 2026-02-25: "if there's a pre-existing comment it doesn't
    // enforce another comment when you are rejecting … so it doesn't
    // force another comment so that it's smooth."
    if (next === "rejected") {
      const hasExistingComment = Array.isArray(workItem.comments) && workItem.comments.some(
        (c) => (typeof c === "string" ? c : c?.text || "").trim().length > 0,
      );
      if (!hasExistingComment) {
        setRejectPromptFor({ item: workItem, taskLabel: workItem.label || "task" });
        return;
      }
      // else: fall through to the normal PATCH below.
    }
    // Optimistic local update
    setJob((j) => j ? {
      ...j,
      checklist: (j.checklist || []).map((it) => it.id === workItem.id ? { ...it, status: next } : it),
    } : j);
    try {
      await api.patch(`/jobcards/${id}/checklist/${workItem.id}`, { status: next });
    } catch (e) {
      // Revert + visible error so user knows why it didn't move
      load();
      toast.error(e?.response?.data?.detail || "Could not update task status.");
    }
  };

  // [Iter 340 — 2026-02-25] Reject-reason prompt state. When set,
  // renders the modal below. Clearing it (Cancel) leaves the task
  // status untouched.
  const [rejectPromptFor, setRejectPromptFor] = useState(null);
  const [rejectReasonDraft, setRejectReasonDraft] = useState("");
  // Iter 398j — same double-submit guard as `addItemComment`. Prevents
  // reject-reasons from being appended twice (Dirk's screenshot showed
  // the "❌ Rejected: …" comment landing 2×).
  const [rejectBusy, setRejectBusy] = useState(false);

  const submitRejectReason = async () => {
    if (rejectBusy) return;
    const reason = (rejectReasonDraft || "").trim();
    if (!reason) {
      toast.error("A rejection reason is required.");
      return;
    }
    const item = rejectPromptFor?.item;
    if (!item) return;
    setRejectBusy(true);
    // Optimistic local update
    setJob((j) => j ? {
      ...j,
      checklist: (j.checklist || []).map((it) => it.id === item.id ? { ...it, status: "rejected" } : it),
    } : j);
    try {
      // Single PATCH does BOTH the status flip and the reason comment
      // so we don't get a half-committed state if the second call
      // fails.
      await api.patch(`/jobcards/${id}/checklist/${item.id}`, {
        status: "rejected",
        add_comment: `❌ Rejected: ${reason}`,
      });
      toast.success("Task rejected with reason.");
      setRejectPromptFor(null);
      setRejectReasonDraft("");
      load();
    } catch (e) {
      load();
      toast.error(e?.response?.data?.detail || "Could not reject task.");
    } finally {
      setRejectBusy(false);
    }
  };

  const cancelRejectPrompt = () => {
    setRejectPromptFor(null);
    setRejectReasonDraft("");
  };

  const addItemComment = async (item) => {
    // Wave 25.15 — match the per-task isolation key used in the textarea.
    const k = String(item.id || "");
    // Iter 398j — hard guard against double-submission (Ctrl+Enter x2,
    // rapid Add clicks, keyboard-and-button race). If a POST is
    // already in flight for THIS task's comment, bail early.
    if (commentBusy[k]) return;
    const txt = (commentDrafts[k] || "").trim();
    if (!txt) return;
    setCommentBusy((prev) => ({ ...prev, [k]: true }));
    // Optimistically clear the draft BEFORE the POST returns so that
    // a lightning-fast second Enter can't repost the same text.
    setCommentDrafts((prev) => ({ ...prev, [k]: "" }));
    try {
      await api.patch(`/jobcards/${id}/checklist/${item.id}`, { add_comment: txt });
      load();
    } catch (err) {
      // Restore the draft so the user doesn't lose their words on a network fail.
      setCommentDrafts((prev) => ({ ...prev, [k]: txt }));
      toast.error(err?.response?.data?.detail || "Could not save comment.");
    } finally {
      setCommentBusy((prev) => {
        const next = { ...prev };
        delete next[k];
        return next;
      });
    }
  };

  // Iter 363 (2026-02-27) — Dirk: eye toggle next to each comment's
  // timestamp. When flipped to hidden, the comment is stripped from
  // the customer portal + PDF but remains on the CRM view for tech &
  // HR. Legacy comments without the flag default to visible.
  const toggleCommentVisibility = async (item, comment) => {
    if (!item?.id || !comment?.id) return;
    const nextVisible = comment.visible_to_customer === false;  // flipping
    try {
      await api.patch(`/jobcards/${id}/checklist/${item.id}`, {
        set_comment_visibility: { id: comment.id, visible: nextVisible },
      });
      load();
    } catch (_e) {
      toast.error("Could not update comment visibility.");
    }
  };

  const [photoUploads, setPhotoUploads] = useState({ done: 0, total: 0, kind: null });

  // Wave 26.8 — addItemPhoto now uses the singleton upload manager via
  // `enqueue()` directly. The attach() callback runs INSIDE the manager
  // so even if the user navigates away from the JC mid-upload, every
  // queued photo still gets linked to the task (no more "I uploaded 15,
  // only 6 are on the JC"). The global UploadDock at the app root
  // shows progress + retry from any route.
  const addItemPhoto = (item, files) => {
    const list = files?.length ? Array.from(files) : files ? [files] : [];
    if (list.length === 0) return;
    setPhotoUploads({ done: 0, total: list.length, kind: `task:${item.id}` });
    const jobId = id;
    list.forEach((file, idx) => {
      enqueueUpload({
        file,
        target_kind: "jobcard_task",
        target_id: `${jobId}:${item.id}`,
        label: `Task photo: ${item.label?.slice(0, 30) || ""}`.trim(),
        // Iter 169 — resume-safe attach. The global handler in
        // /lib/uploadHandlers.js re-runs this PATCH if the upload
        // survives a hard reload via IndexedDB.
        attach_kind: "jobcard_task_photo",
        attach_payload: { jc_id: jobId, task_id: item.id },
        attach: async (_rec) => {
          // Local UI counter — the network call itself is owned by the
          // attach_kind handler. We keep this closure for the in-memory
          // progress-pill UX while the JC page is still mounted.
          setPhotoUploads((p) => ({ ...p, done: p.done + 1 }));
          if (idx === list.length - 1) {
            setTimeout(() => {
              setPhotoUploads({ done: 0, total: 0, kind: null });
              if (typeof load === "function") load();
            }, 400);
          }
        },
      });
    });
  };

  const handleJobPhoto = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setPhotoUploads({ done: 0, total: files.length, kind: "job" });
    const jobId = id;
    files.forEach((file, idx) => {
      enqueueUpload({
        file,
        target_kind: "jobcard_gallery",
        target_id: jobId,
        label: "Job card gallery photo",
        // Iter 169 — resume-safe attach (see notes on addItemPhoto).
        attach_kind: "jobcard_gallery_photo",
        attach_payload: { jc_id: jobId },
        attach: async (_rec) => {
          setPhotoUploads((p) => ({ ...p, done: p.done + 1 }));
          if (idx === files.length - 1) {
            setTimeout(() => {
              setPhotoUploads({ done: 0, total: 0, kind: null });
              if (typeof load === "function") load();
            }, 400);
          }
        },
      });
    });
    // Allow re-selecting the same files later
    e.target.value = "";
  };

  const removePhoto = async (idx) => {
    if (!isManager) return;
    if (!window.confirm("Remove this job photo? This cannot be undone.")) return;
    const photos = job.photos.filter((_, i) => i !== idx);
    await update({ photos });
  };

  const signoff = async (type, signature_data_url, name) => {
    // [Iter 331 — 2026-02-24] Forward the signee's typed name (client
    // sign-off only). Backend already accepts an optional `name`
    // field on `POST /jobcards/{id}/signoff` and stamps `signed_at`.
    const payload = { type, signature_data_url };
    if (name) payload.name = name;
    await api.post(`/jobcards/${id}/signoff`, payload);
    load();
  };

  if (!job) return <div className="text-[#647087]">Loading…</div>;

  const tech = users.find((u) => u.id === job.technician_id);
  const wa = customer?.whatsapp ? customer.whatsapp.replace(/[^0-9]/g, "") : "";
  const tel = customer?.phone ? customer.phone.replace(/[^0-9+]/g, "") : "";
  const mapQ =
    site?.lat && site?.lng
      ? `${site.lat},${site.lng}`
      : site?.address
      ? encodeURIComponent(site.address)
      : null;

  // Phase 7 Sub-iter 7A — EntityShell wrap.
  // Stage B: tabs surface the 6 logical sections of a JC (Overview, Tasks,
  // Materials, Photos, Documents, Chat, History) so users see the same
  // shape on every JC.  The page-level action buttons stay above the
  // tab strip so they're reachable from any tab.
  const JC_STATUS_TONE = {
    open: "blue", scheduled: "blue", in_progress: "amber",
    paused: "violet", on_site_complete: "green",
    complete: "green", cancelled: "slate",
  };
  const _statusLabel = (job?.phase || "open").replaceAll("_", " ");
  const _statusTone = JC_STATUS_TONE[job?.phase] || "slate";
  const _jcTabs = isCreate ? [] : [
    { key: "overview", label: "Overview" },
    { key: "tasks", label: "Tasks", count: (job.checklist || []).length || undefined },
    { key: "materials", label: "Materials" },
    // [Iter 342 — 2026-02-25] Count merged photos (main + all task-inline)
    // deduped by upload_id so the tab count matches the grid the user sees.
    { key: "photos", label: "Photos", count: (() => {
      const ids = new Set();
      const push = (src) => {
        const id = typeof src === "string"
          ? src
          : (src?.upload_id || src?.id || null);
        if (id) ids.add(id);
      };
      (job.photos || []).forEach(push);
      (job.checklist || []).forEach((t) => (t.photos || []).forEach(push));
      return ids.size || undefined;
    })() },
    { key: "documents", label: "Documents", count: (job.documents || []).length || undefined },
    { key: "chat", label: "Chat" },
    { key: "history", label: "Activity" },
  ];

  return (
    <EntityShell
      shortId={job.short_id ? `#${job.short_id}` : undefined}
      kindLabel="Job Card"
      title={job.title || (isCreate ? "New Job Card" : "Job Card")}
      status={!isCreate ? { label: _statusLabel, tone: _statusTone } : undefined}
      customerLink={customer ? {
        label: `View customer · ${customer.name}`,
        to: `/customers/${customer.id}`,
      } : undefined}
      backTo="/jobcards"
      actions={null /* Stage B will hoist the long action cluster here. */}
      tabs={_jcTabs}
      activeTab={tab}
      onTabChange={setTab}
      footerMeta={!isCreate ? {
        createdByName: job.created_by_name,
        createdAtDisplay: job.created_at_display
          || (job.created_at ? new Date(job.created_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : ""),
        updatedByName: job.updated_by_name,
        updatedAtDisplay: job.updated_at_display
          || (job.updated_at ? new Date(job.updated_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : ""),
      } : undefined}
    >
    <div className="space-y-6" data-testid="jobcard-detail">
      {/* Wave 25.16 — floating Edit/Done pill, bottom-LEFT.
          Bottom-RIGHT is reserved for the Hidro launcher. Reaches the user
          wherever they are in the JC so they don't have to scroll back to
          the top toolbar to enter/exit edit. Hidden in create mode (whole
          page is already in edit) and for users with no edit capability.
          Iter 418.q (2026-02-28) — pinned above the mobile bottom-nav
          (h≈64px + safe-area) so it stays sticky on iOS Safari. Inline
          calc(env()) was flaky in Safari inside our sticky/backdrop-blur
          stacking contexts; use plain Tailwind bottom-24 md:bottom-6
          instead. */}
      {!isCreate && (isManager || techStartMode) && (
        <button
          type="button"
          onClick={() => setEditMode((m) => !m)}
          className={`fixed left-3 bottom-24 md:bottom-6 z-[900] hv-btn shadow-lg ${
            editMode
              ? "hv-btn-primary !bg-emerald-500/90 !border-emerald-400/60 hover:!bg-emerald-500"
              : "hv-btn-primary"
          }`}
          title={editMode ? "Exit edit mode (lock the page)" : "Edit this job card"}
          data-testid="jc-floating-edit"
        >
          {editMode ? (
            <><CheckCircle2 className="w-4 h-4" /> Done</>
          ) : (
            <><Pencil className="w-4 h-4" /> Edit</>
          )}
        </button>
      )}
      {/* Top thin coloured border when in edit mode — second visual cue. */}
      {editMode && !isCreate && (
        <div className="fixed inset-x-0 top-0 h-0.5 bg-emerald-400/70 z-[850] pointer-events-none" data-testid="jc-edit-mode-banner" />
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Link to="/jobcards" className="inline-flex items-center gap-2 text-xs text-[#647087] hover:text-white">
          <ArrowLeft className="w-3 h-3" /> Job cards
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          {job?.source_chat_thread_id && (
            <FromChatPill
              threadId={job.source_chat_thread_id}
              messageId={job.source_chat_message_id}
            />
          )}
          {customer && (
            <Link
              to={`/customers/${customer.id}`}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="job-back-to-customer"
            >
              View customer · {customer.name}
            </Link>
          )}
          {/* Iter 418.t — CRM-side download of the customer-portal
              PDF. Saves a hop through the portal when a rep just
              wants to email/whatsapp the customer their JC. */}
          {!isCreate && (
            <button
              type="button"
              onClick={downloadJcPdf}
              disabled={downloadingJcPdf}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="jc-download-client-pdf"
              title="Download the exact PDF the customer sees on the portal"
            >
              <FileDown className="w-3 h-3" />
              {downloadingJcPdf ? "Generating…" : "Client PDF"}
            </button>
          )}
          {!isManager && (
            <span className="text-xs text-amber-300/80 inline-flex items-center gap-1">
              <Lock className="w-3 h-3" /> Field mode — structure is manager-locked
            </span>
          )}
          {isManager && !editMode && (
            <button
              onClick={() => setEditMode(true)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="job-edit-btn"
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
          )}
          {/* Start / Pause / Complete — Wave 19 #82 workflow buttons.
              Visible to assigned techs (and managers as override). State
              changes save immediately so they survive logout/login. */}
          {canStart && (
            <button
              onClick={() => transitionPhase("in_progress")}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="job-start-btn"
              title="Start work — moves status to In Progress and unlocks tech fields"
            >
              <Play className="w-3 h-3" /> Start
            </button>
          )}
          {canPause && (
            <button
              onClick={() => transitionPhase("paused")}
              className="hv-btn hv-btn-ghost text-xs text-amber-200"
              data-testid="job-pause-btn"
              title="Pause work — status becomes Paused; resume to keep editing"
            >
              <Pause className="w-3 h-3" /> Pause
            </button>
          )}
          {canResume && (
            <button
              onClick={() => transitionPhase("in_progress")}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="job-resume-btn"
              title="Resume work — back to In Progress"
            >
              <Play className="w-3 h-3" /> Resume
            </button>
          )}
          {canFinishOnSite && (
            <button
              onClick={() => transitionPhase("on_site_complete")}
              className="hv-btn hv-btn-primary text-xs bg-emerald-600 hover:bg-emerald-500"
              data-testid="job-complete-btn"
              title="Mark on-site complete — locks tech UI and notifies the team"
            >
              <CheckCircle2 className="w-3 h-3" /> Complete on site
            </button>
          )}
          {(job?.phase === "in_progress" || job?.phase === "paused" || job?.phase === "complete") && !techCanComplete && (
            <span className="text-[10px] text-amber-300/80 inline-flex items-center gap-1 max-w-[420px]" data-testid="job-complete-hint">
              <Lock className="w-3 h-3 shrink-0" />
              <span>
                To Complete, capture: <span className="text-amber-200 font-medium">{_missingForComplete.join(", ")}</span>
              </span>
            </span>
          )}
          {isOnSiteComplete && techRoleAllowed && !isManager && (
            <span className="text-[10px] text-emerald-300/80 inline-flex items-center gap-1" data-testid="job-locked-after-complete">
              <Lock className="w-3 h-3" /> Locked — manager must reopen to edit
            </span>
          )}
          {isOnSiteComplete && isManager && (
            <button
              onClick={() => transitionPhase("in_progress")}
              className="hv-btn hv-btn-ghost text-xs text-amber-200"
              data-testid="job-reopen-btn"
              title="Re-open this job card so the technician can keep editing"
            >
              <RotateCcw className="w-3 h-3" /> Re-open
            </button>
          )}
          {isManager && editMode && (
            <>
              <button
                onClick={handleCancel}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="job-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="hv-btn hv-btn-primary text-xs"
                data-testid="job-save-btn"
              >
                <Save className="w-3 h-3" /> Save
              </button>
            </>
          )}
          {isManager && !isCreate && !confirmDelete && (
            <button
              onClick={() => setConfirmDelete(true)}
              className="hv-btn hv-btn-ghost text-rose-300 hover:text-rose-200 text-xs"
              data-testid="job-delete-btn"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          )}
          {isManager && !isCreate && confirmDelete && (
            <>
              <button
                onClick={() => setConfirmDelete(false)}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="job-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="hv-btn hv-btn-primary bg-rose-500 hover:bg-rose-400 text-xs"
                data-testid="job-delete-confirm"
              >
                <Trash2 className="w-3 h-3" /> Confirm delete
              </button>
            </>
          )}
          {!isCreate && <AddToCalendarButton kind="jobcard" id={job?.id} compact />}
        </div>
      </div>

      {(isCreate || tab === "overview") && (
      <div className="hv-card p-5 md:p-6">
        {/* Wave 29 Chunk 10p — site pin capture prompt. Asks every JC
            open until the customer has a saved location (or the JC's
            site_pin is recorded). Tech can Skip; we re-prompt on next
            page load. */}
        <SitePinPrompt
          job={job}
          customer={customer}
          onCaptured={async (loc) => {
            try {
              const { data } = await api.post(`/jobcards/${job.id}/site-pin`, loc);
              setJob((j) => ({ ...j, site_pin: data.site_pin }));
              if (data.customer_updated && customer) {
                setCustomer({ ...customer, lat: loc.lat, lng: loc.lng });
              }
            } catch (e) {
              alert(e?.response?.data?.detail || "Could not save site pin.");
            }
          }}
        />
        {/* Wave 29 Phase F.1 — Location mismatch banner.
            Shows when the JC's captured start or complete location is
            >200m from the saved customer coords. Banner colour:
            soft_mismatch = amber, hard_mismatch = red. */}
        {(() => {
          const loc = job?.start_location || job?.complete_location || job?.latest_location;
          if (!loc || !loc.audit) return null;
          if (loc.audit === "on_site") return null;
          const isHard = loc.audit === "hard_mismatch";
          const isSoft = loc.audit === "soft_mismatch";
          const isDenied = loc.denied || loc.audit === "denied";
          const noSaved = loc.audit === "no_saved_location";
          if (!isHard && !isSoft && !isDenied && !noSaved) return null;
          const tone = isHard
            ? "border-red-500/40 bg-red-500/10 text-red-200"
            : isDenied
            ? "border-amber-400/30 bg-amber-400/[0.06] text-amber-200"
            : "border-amber-500/40 bg-amber-500/10 text-amber-200";
          const distKm = loc.distance_m != null ? (loc.distance_m / 1000) : null;
          let msg;
          if (isDenied) msg = "Tech started this job without sharing GPS. Flagged for review.";
          else if (noSaved) msg = "Customer has no saved coordinates yet — tap to add this site's pin.";
          else if (isHard) msg = `This job was started ${distKm?.toFixed(1)} km from the saved customer address.`;
          else msg = `This job was started ${Math.round(loc.distance_m || 0)} m from the saved customer address.`;
          return (
            <Link
              to={`/jobcards/${job?.id}/site-mismatch`}
              className={`block text-xs border ${tone} rounded px-3 py-2 mb-3 hover:opacity-90`}
              data-testid="job-location-mismatch-banner"
            >
              <span className="font-semibold">⚠ Location check —</span> {msg}{" "}
              <span className="underline">Open map →</span>
            </Link>
          );
        })()}
        {/* CREATE MODE — error banner from missing fields */}
        {isCreate && createErr && (
          <div className="text-xs text-rose-300 border border-rose-500/30 bg-rose-500/10 rounded px-2 py-1.5 mb-3" data-testid="job-create-err">
            {createErr}
          </div>
        )}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <PhaseChip value={job.phase} />
            {/* [Iter 420] Reverse project-link chip. Sits next to the
                phase chip so techs and managers can see or set the
                project anchor without leaving the JC. */}
            {!isCreate && job.id && (
              <span className="ml-2 inline-block align-middle">
                <ProjectLinkChip
                  kind="jobcard"
                  entityId={job.id}
                  projectId={job.project_id}
                  customerId={job.customer_id}
                  onLinked={() => load && load()}
                  onUnlinked={() => load && load()}
                />
              </span>
            )}
            {/* Wave 25.13 — at-a-glance task completion bar in the header.
                Same multi-colour stack as on the JC list rows. Hidden in
                create-mode (no checklist yet). */}
            {!isCreate && (job.checklist || []).length > 0 && (
              <div className="mt-3" data-testid="jc-summary-progress">
                <JCProgressBar tasks={job.checklist || []} />
              </div>
            )}
            {/* Title — editable when in edit mode (also covers the create
                template since editMode defaults true for `id==="new"`). */}
            {canEditMeta ? (
              <input
                type="text"
                value={job.title || ""}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="Job card title (e.g. Replace booster pump and pressure tank)"
                className="hv-input mt-2 text-2xl md:text-3xl font-display font-bold tracking-tight w-full"
                data-testid="job-title-input"
              />
            ) : (
              <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2 flex items-center gap-3 flex-wrap">
                {job.short_id && (
                  <span className="text-base font-mono text-amber-300/80">#{job.short_id}</span>
                )}
                {job.title || <span className="text-[#647087]">(no title)</span>}
                {job.title && <ReadAloudButton text={job.title} size="sm" testId="job-title-tts" />}
              </h1>
            )}
            {/* Customer + Site — DESIGN_CONTRACT §1: every job card lives
                under a customer + site. Editable in edit mode (so a manager
                can repoint a misfiled JC) and required in create mode. */}
            {canEditMeta ? (
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Customer *</label>
                  <SearchableSelect
                    value={job.customer_id || ""}
                    onChange={(cid) => update({ customer_id: cid })}
                    options={(allCustomers || []).map((c) => ({
                      id: c.id,
                      label: c.name,
                      sublabel: [c.external_ref && `Acc# ${c.external_ref}`, c.vat_number && `VAT ${c.vat_number}`, c.phone, c.email].filter(Boolean).join(" · "),
                      searchTerms: [c.name, c.external_ref, c.vat_number, c.phone, c.whatsapp, c.email, c.physical_address, c.legal_name, c.short_id].filter(Boolean),
                    }))}
                    placeholder="— pick a customer —"
                    required
                    testId="job-customer-input"
                    emptyHint="No customers match. Try VAT, account no., phone or email."
                    onCreateNew={(q) => {
                      navigate(`/customers/new?name=${encodeURIComponent(q || "")}&from=jobcards/${isCreate ? "new" : id}`);
                    }}
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Site *</label>
                  <SearchableSelect
                    value={job.site_id || ""}
                    onChange={(sid) => update({ site_id: sid })}
                    options={(allSitesForCustomer || []).map((s) => ({
                      id: s.id,
                      label: s.name,
                      sublabel: s.address || "",
                      searchTerms: [s.name, s.address, s.external_ref].filter(Boolean),
                    }))}
                    placeholder={
                      !job.customer_id
                        ? "— pick a customer first —"
                        : (allSitesForCustomer || []).length === 0
                          ? "— customer has no sites —"
                          : "— pick a site —"
                    }
                    disabled={!job.customer_id || (allSitesForCustomer || []).length === 0}
                    required
                    testId="job-site-input"
                    emptyHint="No sites match this search."
                    onCreateNew={job.customer_id ? () => {
                      navigate(`/customers/${job.customer_id}?focus=sites&from=jobcards/${isCreate ? "new" : id}`);
                    } : undefined}
                    createNewLabel={job.customer_id ? "Add a site to this customer" : undefined}
                  />
                  {job.customer_id && (allSitesForCustomer || []).length === 0 && (
                    <div className="text-[11px] text-amber-300 mt-1 inline-flex items-center gap-1">
                      No sites yet. <Link to={`/customers/${job.customer_id}?focus=sites`} className="underline">Add the site first</Link>.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-[#a0aabf] mt-1">
                {customer?.name} {site && <>· {site.name}</>}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {tel && (
              <a
                href={phoneToWa(tel) || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="hv-btn hv-btn-ghost"
                data-testid="job-call-btn"
                title="Call (opens WhatsApp)"
              >
                <Phone className="w-4 h-4" /> Call
              </a>
            )}
            {wa && (
              <a
                href={`https://wa.me/${wa}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hv-btn hv-btn-ghost"
                data-testid="job-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            )}
            {mapQ && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${mapQ}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hv-btn hv-btn-primary"
                data-testid="job-navigate-btn"
              >
                <Navigation className="w-4 h-4" /> Navigate
              </a>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-3 mt-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#647087]">Phase</div>
            {canEditPhase ? (
              <select
                value={job.phase}
                onChange={(e) => update({ phase: e.target.value })}
                className="hv-input mt-1"
                data-testid="job-phase-select"
              >
                {phaseOptionsFor(job.phase, user?.role === "technician").map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            ) : (
              <div className="hv-input mt-1 opacity-70">{job.phase.replaceAll("_", " ")}</div>
            )}
            {job.phase === "quote_client" && (
              <div className="mt-1 text-[10px] text-rose-300 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Legacy AppSheet phase — replace with a current phase.
              </div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[#647087]">Technician (sign-off)</div>
            {canEditMeta ? (
              <SearchableSelect
                value={job.technician_id || ""}
                onChange={(id) => update({ technician_id: id || null })}
                options={users.map((u) => ({
                  id: u.id,
                  label: u.name || u.email || u.id,
                  sublabel: [u.role, u.email, u.phone].filter(Boolean).join(" · "),
                  searchTerms: [u.name, u.email, u.phone, u.role, u.short_id].filter(Boolean),
                }))}
                placeholder="— Unassigned —"
                testId="job-tech-select"
              />
            ) : (
              <div className="hv-input mt-1 opacity-70">{tech?.name || "Unassigned"}</div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[#647087]">Team members on site</div>
            <TeamMembersPicker
              value={job.staff_ids || []}
              users={users}
              excludeUserId={job.technician_id}
              canEdit={canEditMeta}
              onChange={(ids) => update({ staff_ids: ids })}
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[#647087]">Vehicle</div>
            <VehiclePicker
              value={job.vehicle_id || ""}
              startsAt={job.scheduled_at}
              endsAt={job.scheduled_end}
              jobcardId={job.id}
              canEdit={canEditMeta}
              onChange={(vid) => update({ vehicle_id: vid || null })}
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[#647087]">Start</div>
            {canEditMeta ? (
              <DateTimePicker
                value={job.scheduled_at}
                onChange={(v) => update({ scheduled_at: v || null })}
                placeholder="Pick start"
                testId="job-detail-start"
                className="mt-1"
              />
            ) : (
              <div className="hv-input mt-1 opacity-70">
                {job.scheduled_at ? new Date(job.scheduled_at).toLocaleString('en-GB') : "—"}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[#647087]">End (est.)</div>
            {canEditMeta ? (
              <DateTimePicker
                value={job.scheduled_end}
                onChange={(v) => update({ scheduled_end: v || null })}
                placeholder="Pick end"
                testId="job-detail-end"
                className="mt-1"
              />
            ) : (
              <div className="hv-input mt-1 opacity-70">
                {job.scheduled_end ? new Date(job.scheduled_end).toLocaleString('en-GB') : "—"}
              </div>
            )}
          </div>
        </div>

        {/* Phase A: Customer special requests visible on the JC (alert-style) */}
        {customer?.special_requests && (
          <div
            className="mt-4 px-3 py-2 rounded border border-amber-500/40 bg-amber-500/5 text-sm text-amber-100/90 whitespace-pre-wrap"
            data-testid="job-customer-special-requests"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold mb-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Customer special requests
            </div>
            {customer.special_requests}
          </div>
        )}

        {/* Phase A: Time / Skills / Invoice / Special instructions — always visible for managers */}
        <div className="mt-5 grid md:grid-cols-3 gap-3" data-testid="job-extra-fields">
            <NumField
              label="Time allocation (hrs)"
              value={job.time_allocation_hours}
              onChange={(v) => update({ time_allocation_hours: v })}
              icon={Clock}
              isManager={canEditMeta}
              testid="job-time-allocation"
              hint={
                job.time_allocation_hours != null
                  ? `≈ ${formatHoursAsHHmm(job.time_allocation_hours)}`
                  : "Auto-fills from Start/End if both are set"
              }
            />
            <NumField
              label="Hours spent"
              value={job.hours_spent}
              onChange={(v) => update({ hours_spent: v })}
              icon={Clock}
              isManager={canEditMeta}
              testid="job-hours-spent"
            />
            <NumField
              label="Hours to invoice"
              value={job.hours_to_invoice}
              onChange={(v) => update({ hours_to_invoice: v })}
              icon={Receipt}
              isManager={canEditMeta}
              testid="job-hours-invoice"
            />
            <div className="md:col-span-3">
              <div className="text-xs uppercase tracking-wider text-[#647087] flex items-center gap-1">
                <Wrench className="w-3 h-3" /> Required skills
              </div>
              {canEditMeta ? (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2" data-testid="job-required-skills-picker">
                  {REQUIRED_SKILLS.map((sk) => {
                    const checked = (job.required_skills || []).includes(sk);
                    return (
                      <label
                        key={sk}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded border cursor-pointer text-xs transition ${
                          checked
                            ? "border-[#1fb6c1] bg-[#1fb6c1]/10 text-[#1fb6c1]"
                            : "border-white/10 text-[#a0aabf] hover:border-white/20"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(job.required_skills || []);
                            e.target.checked ? next.add(sk) : next.delete(sk);
                            update({ required_skills: Array.from(next) });
                          }}
                          className="hidden"
                        />
                        {sk}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="hv-input mt-1 opacity-70 min-h-[40px]">
                  {(job.required_skills || []).length === 0
                    ? "—"
                    : job.required_skills.join(" · ")}
                </div>
              )}
            </div>
            <TextField
              label="Job card reason"
              value={job.job_card_reason || ""}
              onChange={(v) => update({ job_card_reason: v })}
              isManager={canEditMeta}
              testid="job-reason"
            />
            <div className="md:col-span-3 grid sm:grid-cols-2 gap-3" data-testid="job-source-block">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#647087]">Related support ticket</div>
                <RelatedTicketField
                  job={job}
                  customer={customer}
                  canEdit={canEditMeta}
                  onChange={(val) => update({ related_support_ticket: val })}
                />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#647087]">Related sales order</div>
                <RelatedSalesOrderField
                  job={job}
                  customer={customer}
                  canEdit={canEditMeta}
                  onChange={(val) => update({ related_sales_order: val })}
                />
              </div>
              {canEditMeta && !job.related_sales_order && !job.related_support_ticket && (
                <div className="sm:col-span-2 text-[10px] text-amber-300/80 inline-flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Link a support ticket and/or a sales order so this job card has a clear origin.
                </div>
              )}
              <div className="sm:col-span-2 border-t border-white/5 pt-3 mt-1 space-y-3">
                {/* Iter 418.bf (Dirk, 2026-02-28) — Many-to-many links.
                    A JC can now belong to multiple support tickets and
                    multiple sales orders. Legacy singular fields above
                    stay in place as the "primary" link (used by the
                    email dispatcher etc.); these chip rows carry the
                    array-form links via the entity-links pipeline and
                    surface every linked doc in the family tree. */}
                <MultiLinkChipRow
                  entityKind="jobcard"
                  entityId={job.id}
                  customerId={customer?.id}
                  targetKind="ticket"
                  targetIds={[
                    ...(job.ticket_ids || []),
                    ...(job.related_support_ticket && !(job.ticket_ids || []).includes(job.related_support_ticket)
                      ? [job.related_support_ticket] : []),
                  ]}
                  canEdit={canEditMeta}
                  label="Additional linked tickets"
                  onChanged={load}
                />
                <MultiLinkChipRow
                  entityKind="jobcard"
                  entityId={job.id}
                  customerId={customer?.id}
                  targetKind="sales_doc"
                  targetIds={[
                    ...(job.sales_order_ids || []),
                    ...(job.related_sales_order && !(job.sales_order_ids || []).includes(job.related_sales_order)
                      ? [job.related_sales_order] : []),
                  ]}
                  canEdit={canEditMeta}
                  label="Additional linked sales orders"
                  onChanged={load}
                />
              </div>
            </div>
            {job.external_ref && (
              <div>
                <div className="text-xs uppercase tracking-wider text-[#647087]">AppSheet ref</div>
                <div className="hv-input mt-1 opacity-70 font-mono text-xs">{job.external_ref}</div>
              </div>
            )}
            <div className="md:col-span-3">
              <div className="text-xs uppercase tracking-wider text-[#647087]">Special instructions</div>
              {canEditMeta ? (
                <BlurTextarea
                  value={job.special_instructions || ""}
                  onSave={(v) => update({ special_instructions: v })}
                  testid="job-special-instructions"
                />
              ) : (
                <div className="hv-input mt-1 opacity-70 min-h-[40px] whitespace-pre-wrap">
                  {job.special_instructions || "—"}
                </div>
              )}
            </div>
            <div className="md:col-span-3">
              <div className="text-xs uppercase tracking-wider text-[#647087]">Invoice notes</div>
              {canEditMeta ? (
                <BlurTextarea
                  value={job.invoice_notes || ""}
                  onSave={(v) => update({ invoice_notes: v })}
                  testid="job-invoice-notes"
                />
              ) : (
                <div className="hv-input mt-1 opacity-70 min-h-[40px] whitespace-pre-wrap">
                  {job.invoice_notes || "—"}
                </div>
              )}
            </div>
          </div>
      </div>
      )}

      {/* Materials tab — dedicated pane for the materials ledger + Add material + Generate picking + linked picking lists. */}
      {!isCreate && tab === "materials" && (
        <div className="space-y-4" data-testid="jc-materials-tab">
          <JobCardMaterialsBlock
            job={job}
            canEdit={isManager || user?.role === "technician"}
            onChanged={load}
            user={user}
          />
          <EntityLinksPanel
            entityType="jobcard"
            entityId={job.id}
            linkTo={["picking", "sales_doc"]}
          />
        </div>
      )}

      {(isCreate || tab === "tasks") && (
      <div className="hv-card p-5">
        {/* [Iter 349] Task header row — includes the new "My tasks"
            filter toggle. Kept mobile-first: title on the left, filter
            on the right so a tech's thumb can flip it without hunting
            through a menu. */}
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-display text-xl font-bold">To-do</h3>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Iter 418.d (Dirk, 2026-02-28) — Status filter dropdown.
                Native <select> so mobile pops the OS wheel picker. Shows
                the count of matching tasks so a tech can gauge scope at
                a glance. Only rendered when there's at least one task. */}
            {(job.checklist || []).length > 0 && (() => {
              const list = job.checklist || [];
              const total = list.length;
              const byStatus = {
                todo: list.filter((t) => !t.status || t.status === "pending").length,
                in_progress: list.filter((t) => t.status === "in_progress").length,
                done: list.filter((t) => t.status === "done" || t.status === "complete").length,
                // Iter 418.bk (Dirk, 2026-02-28) — Rejected filter so
                // managers can see just the tasks the tech refused +
                // read the reason without scrolling past everything
                // completed.
                rejected: list.filter((t) => t.status === "rejected").length,
              };
              return (
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="hv-input text-xs py-1 pl-2 pr-6"
                  data-testid="jc-status-filter"
                  title="Filter tasks by status"
                >
                  <option value="all">All ({total})</option>
                  <option value="todo">To-do ({byStatus.todo})</option>
                  <option value="in_progress">In progress ({byStatus.in_progress})</option>
                  <option value="done">Completed ({byStatus.done})</option>
                  <option value="rejected">Rejected ({byStatus.rejected})</option>
                </select>
              );
            })()}
            {(job.checklist || []).length > 0 && (() => {
            const total = job.checklist.length;
            const mine = job.checklist.filter((t) => taskInMyList(t, user?.id)).length;
            return (
              <button
                type="button"
                onClick={() => setMyTasksOnly((v) => !v)}
                className={`text-xs px-2.5 py-1 rounded-md border transition ${
                  myTasksOnly
                    ? "border-[#1FB6C1]/50 bg-[#1FB6C1]/15 text-[#a0e6ea]"
                    : "border-white/10 bg-white/[0.03] text-[#8d94a8] hover:text-white"
                }`}
                data-testid="jc-my-tasks-toggle"
                title={
                  myTasksOnly
                    ? "Showing only tasks assigned to you or open to anyone"
                    : "Showing all tasks on this job card"
                }
              >
                {myTasksOnly ? "My tasks · " : "My tasks "}
                <span className="opacity-75 tabular-nums">
                  {myTasksOnly ? `${mine}` : `(${mine}/${total})`}
                </span>
              </button>
            );
          })()}
          </div>
        </div>
        {/* Iter 349.f (2026-02-25) — Tech-added ad-hoc task/comment
            entry point (TOP). Sits above the tasks list so a
            technician can quickly log an unplanned finding without
            scrolling past everything. Managers see it too but the
            button toasts them a hint if they try to use it.
            Iter 349.h — onCreated now auto-flips the new row into
            edit mode + focuses its label editor (mirrors addItem)
            so the tech can type immediately. */}
        {!isCreate && job.id && (
          <div className="mb-3">
            <AddTechNoteButton
              jobId={job.id}
              variant="top"
              onCreated={handleTechNoteCreated}
            />
          </div>
        )}
        <div className="space-y-3">
          {(job.checklist || [])
            .map((it, i) => ({ it, i }))
            .filter(({ it }) => !myTasksOnly || taskInMyList(it, user?.id))
            .filter(({ it }) => {
              // Iter 418.d — status filter.
              if (statusFilter === "all") return true;
              if (statusFilter === "todo") return !it.status || it.status === "pending";
              if (statusFilter === "in_progress") return it.status === "in_progress";
              if (statusFilter === "done") return it.status === "done" || it.status === "complete";
              if (statusFilter === "rejected") return it.status === "rejected";
              return true;
            })
            .map(({ it, i }) => {
            const done = it.status === "done" || it.status === "complete";
            const inProgress = it.status === "in_progress";
            // [Iter 340 — 2026-02-25] Rejected — red style, mirrors the
            // TaskStatusSelector pill.
            const rejected = it.status === "rejected";
            // Wave 25.16 (decision: yes) — task status pills are quick-actions,
            // not edits. ANY user with JC access (incl. unassigned techs and
            // reps) can flip Pending / In progress / Complete so a team member
            // who jumped on the job ahead of plan can mark it without waiting
            // for the assigned tech to "Start" the JC. This is intentionally
            // separate from the new Wave 25.16 view-mode lock — quick-actions
            // are always-on; signature pads / titles / customer reassignment
            // are still gated.
            const techCanTick = true;
            // Wave 26.10.8 — view/edit mode. Edit mode is per-task and
            // unlocks the comment composer + (for managers) the title
            // textarea. Default = view mode so scrolling on mobile can't
            // accidentally erase a task or its title.
            const isEditing = editingTaskId === (it.id || `idx-${i}`);
            // Iter 427.p38 — Managers can drag any row to reorder;
            // the grip handle is what the user grabs. We disable
            // drag while a title textarea is being edited so mouse-
            // selecting text inside the editor never accidentally
            // starts a drag.
            const dragEnabled = isManager && !isEditing;
            const isDragTarget = dragOverIdx === i && dragIdx !== null && dragIdx !== i;

            // ── Iter 349.i (2026-02-25) — Tech-note COMMENT rendering ──
            // Per Dirk: "if the user selects comment, it must only
            // open a plain text field to add a comment. That's it."
            // So tech_note items with note_type=comment render as a
            // COMPACT comment card (mirroring the existing per-task
            // comment layout at ~line 2394), NOT as a full task row:
            //   • no status pills / rejected flow
            //   • no title editor / no drag handle
            //   • single-textarea composer using full row width
            //   • same PhotoPickerButton the regular task rows use
            // Only the author (or a manager) can edit / delete their
            // own note. Everyone with JC access can view it.
            if (it.kind === "tech_note" && it.note_type === "comment") {
              const isAuthor = it.added_by_id && user?.id && it.added_by_id === user.id;
              const canEdit = isAuthor || isManager;
              const draftText =
                labelDrafts[i] !== undefined
                  ? labelDrafts[i]
                  : (it.label || "");
              const isPlaceholder =
                (it.label || "").startsWith("(Tap to describe");
              return (
                <div
                  key={it.id || i}
                  className={`border rounded-md p-2.5 sm:p-3 transition ${
                    isEditing
                      ? "border-[#1FB6C1]/50 bg-[#1FB6C1]/[0.04]"
                      : "border-white/10 bg-white/[0.03]"
                  }`}
                  data-testid={`checklist-item-${i}`}
                >
                  {/* Header row: TECH badge + author + timestamp + edit / delete */}
                  <div className="flex items-center justify-between gap-2 mb-2 text-xs">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span
                        className="shrink-0 inline-flex items-center gap-1 h-5 px-1.5 rounded text-[9px] font-semibold uppercase tracking-wider border border-[#1FB6C1]/40 bg-[#1FB6C1]/10 text-[#a0e6ea]"
                        data-testid={`task-tech-note-badge-${it.id || i}`}
                      >
                        Tech · Note
                      </span>
                      <span
                        className="text-[#1fb6c1] truncate"
                        title={it.added_by_name || ""}
                      >
                        {it.added_by_name || "Technician"}
                      </span>
                      <span className="text-[#647087] shrink-0">
                        · {it.added_at ? new Date(it.added_at).toLocaleString("en-GB") : ""}
                      </span>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-0.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => setEditingTaskId(isEditing ? null : (it.id || `idx-${i}`))}
                          className={`min-h-[36px] min-w-[36px] inline-flex items-center justify-center rounded-md text-sm font-medium ${
                            isEditing
                              ? "bg-[#1FB6C1]/15 text-[#1FB6C1] border border-[#1FB6C1]/40"
                              : "text-[#a0aabf] hover:text-[#1FB6C1] active:bg-white/5 border border-white/10"
                          }`}
                          data-testid={`task-edit-${it.id || i}`}
                          title={isEditing ? "Done editing" : "Edit comment"}
                          aria-label={isEditing ? "Done editing" : "Edit comment"}
                        >
                          {isEditing ? (
                            <Save className="w-4 h-4" />
                          ) : (
                            <Pencil className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm("Delete this comment?")) removeItem(i);
                          }}
                          className="min-h-[36px] min-w-[36px] inline-flex items-center justify-center rounded-md text-rose-300/80 hover:text-rose-300 active:bg-rose-500/10 border border-white/10"
                          data-testid={`task-delete-${it.id || i}`}
                          title="Delete comment"
                          aria-label="Delete comment"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Body — edit mode = textarea, view mode = read-only text */}
                  {canEdit && isEditing ? (
                    <VoiceTextarea
                      value={isPlaceholder && draftText === "" ? "" : draftText}
                      onChange={(next) => renameItem(i, next)}
                      onBlur={(e) => renameItem(i, e?.target?.value, true)}
                      placeholder="Type your comment… (tap the mic to dictate)"
                      rows={3}
                      context="jc_task_comment"
                      className="hv-input w-full py-2 px-3 text-base md:text-sm resize-y min-h-[88px] leading-relaxed"
                      data-testid={`task-title-${it.id || i}`}
                      testIdPrefix={`task-title-${it.id || i}`}
                    />
                  ) : (
                    <div
                      className={`text-base md:text-sm leading-relaxed break-words whitespace-pre-wrap ${
                        isPlaceholder ? "text-[#647087] italic" : "text-[#dde3ec]"
                      }`}
                      data-testid={`task-comment-body-${it.id || i}`}
                    >
                      {isPlaceholder ? "Tap the pencil to add a comment." : it.label}
                    </div>
                  )}

                  {/* Photo strip + attach button */}
                  {(it.photos || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(it.photos || []).map((p, pi) => (
                        <button
                          key={`${p}-${pi}`}
                          type="button"
                          onClick={() =>
                            setLightbox({ urls: (it.photos || []).map((u) => uploadUrl(u)), index: pi })
                          }
                          className="relative w-16 h-16 rounded overflow-hidden border border-white/10 bg-black/30 hover:border-[#1FB6C1]/60"
                          data-testid={`task-photo-thumb-${it.id || i}-${pi}`}
                          title="View photo"
                        >
                          <img
                            src={uploadUrl(p)}
                            alt="attachment"
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                  {canEdit && (
                    <div className="mt-2 flex items-center gap-2">
                      <PhotoPickerButton
                        onFiles={(files) => attachTaskPhotos(it, files)}
                        className="min-h-[36px] px-3 inline-flex items-center gap-1.5 rounded-md text-xs text-[#a0aabf] hover:text-[#1FB6C1] border border-white/10 hover:border-[#1FB6C1]/40 active:bg-white/5 cursor-pointer"
                        title="Attach photo"
                        ariaLabel="Attach photo"
                        testId={`task-photo-${it.id || i}`}
                      >
                        <CameraIcon className="w-4 h-4" />
                        Attach photo
                      </PhotoPickerButton>
                    </div>
                  )}
                </div>
              );
            }
            // ── End tech-note COMMENT branch ──

            return (
              <div
                key={it.id || i}
                className={`border rounded-md p-3 transition ${
                  rejected ? "border-rose-500/40 bg-rose-500/5" :
                  done ? "border-emerald-500/40 bg-emerald-500/5" :
                  inProgress ? "border-sky-400/40 bg-sky-500/5" :
                  isEditing ? "border-[#1FB6C1]/50 bg-[#1FB6C1]/5" :
                  "border-white/10"
                } ${isDragTarget ? (dragIdx < i ? "border-b-4 border-b-[#1FB6C1]" : "border-t-4 border-t-[#1FB6C1]") : ""} ${dragIdx === i ? "opacity-40" : ""}`}
                data-testid={`checklist-item-${i}`}
                onDragOver={dragEnabled ? (e) => {
                  if (dragIdx == null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  if (dragOverIdx !== i) setDragOverIdx(i);
                } : undefined}
                onDragLeave={dragEnabled ? () => {
                  if (dragOverIdx === i) setDragOverIdx(null);
                } : undefined}
                onDrop={dragEnabled ? (e) => {
                  e.preventDefault();
                  const from = dragIdx;
                  setDragOverIdx(null);
                  setDragIdx(null);
                  if (from == null || from === i) return;
                  moveTaskTo(from, i);
                } : undefined}
              >
                {/* Iter 349.i (2026-02-25) — TECH badge on its own
                    horizontal row for tech_note task_done items so
                    the label below has full width to breathe. On
                    narrow mobile the inline placement was pushing
                    the label into a 5-char-wide column and wrapping
                    character-by-character. */}
                {it.kind === "tech_note" && it.note_type === "task_done" && (
                  <div className="flex items-center gap-2 mb-2 text-[10px]">
                    <span
                      className="inline-flex items-center gap-1 h-5 px-1.5 rounded text-[9px] font-semibold uppercase tracking-wider border border-emerald-400/40 bg-emerald-400/10 text-emerald-200"
                      data-testid={`task-tech-note-badge-${it.id || i}`}
                      title={
                        it.added_by_name
                          ? `Added by ${it.added_by_name}${
                              it.added_at
                                ? " · " + new Date(it.added_at).toLocaleString()
                                : ""
                            }`
                          : "Added by technician on site"
                      }
                    >
                      Tech · Task
                    </span>
                    {it.added_by_name && (
                      <span className="text-[#1fb6c1] truncate">
                        {it.added_by_name}
                      </span>
                    )}
                    {it.added_at && (
                      <span className="text-[#647087] truncate">
                        · {new Date(it.added_at).toLocaleString("en-GB")}
                      </span>
                    )}
                  </div>
                )}
                {/* Wave 27.1 — Mobile-first task row.
                    View mode (default): title + camera (wet-hands quick
                    snap) + edit pencil. That's it. Status pills below.
                    Edit mode: title becomes textarea, action bar reveals
                    ↑ ↓ 🗑 + photo. Comment compose stacks below.
                    Goal: usable with gloves / dirty fingers in the field. */}
                <div className="flex items-start gap-2">
                  {/* Iter 427.p38 — Drag handle. Only the handle
                      itself starts drag, not the whole row, so
                      clicking a comment / textarea / photo doesn't
                      accidentally initiate a drag. Manager+ only. */}
                  {dragEnabled && (
                    <button
                      type="button"
                      draggable
                      onDragStart={(e) => {
                        setDragIdx(i);
                        e.dataTransfer.effectAllowed = "move";
                        try { e.dataTransfer.setData("text/plain", String(i)); } catch { /* Safari */ }
                      }}
                      onDragEnd={() => {
                        setDragIdx(null);
                        setDragOverIdx(null);
                      }}
                      className="shrink-0 min-h-[28px] px-1 -ml-0.5 inline-flex items-center justify-center rounded-md text-[#647087] hover:text-[#1FB6C1] cursor-grab active:cursor-grabbing"
                      title="Drag to reorder"
                      aria-label={`Drag task ${i + 1}`}
                      data-testid={`task-drag-${it.id || i}`}
                      // Prevent the click-to-focus behaviour on the
                      // wrapping form from stealing the drag.
                      onClick={(e) => e.preventDefault()}
                    >
                      <GripVertical className="w-4 h-4" />
                    </button>
                  )}
                  {/* [Iter 331 — 2026-02-24] Task number.
                      Mobile-safe: fixed pill width, tabular numbers,
                      generous padding — sequential 1, 2, 3… so techs
                      and clients can reference a specific task
                      unambiguously ("what happened in task 4?"). */}
                  <span
                    className={`shrink-0 select-none inline-flex items-center justify-center min-w-[28px] h-7 px-1.5 rounded-md text-xs font-mono tabular-nums font-semibold border ${
                      rejected
                        ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
                        : done
                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                        : inProgress
                        ? "border-sky-400/40 bg-sky-500/10 text-sky-300"
                        : "border-white/10 bg-white/5 text-[#a0aabf]"
                    }`}
                    data-testid={`task-num-${it.id || i}`}
                    aria-label={`Task ${i + 1}`}
                    title={`Task ${i + 1}`}
                  >
                    {i + 1}
                  </span>
                  {/* Iter 349.i — TECH badge for task_done rows was
                      moved OUT of this flex header to a dedicated row
                      above (see below), because on narrow mobile the
                      badge stole so much width that the task label
                      wrapped character-by-character. */}
                  {(isManager || (it.kind === "tech_note" && it.added_by_id === user?.id)) && isEditing ? (
                    <div className="flex-1 min-w-0">
                      <VoiceTextarea
                        value={labelDrafts[i] !== undefined ? labelDrafts[i] : it.label}
                        onChange={(next) => renameItem(i, next)}
                        onBlur={(e) => renameItem(i, e?.target?.value, true)}
                        placeholder={
                          it.kind === "tech_note"
                            ? (it.note_type === "task_done"
                                ? "Describe what you did on site…"
                                : "Describe what you observed on site…")
                            : "Task title…"
                        }
                        rows={3}
                        context="task_title"
                        className="py-2 px-3 text-base md:text-sm leading-relaxed resize-y break-words min-h-[72px]"
                        data-testid={`task-title-${it.id || i}`}
                        testIdPrefix={`task-title-${it.id || i}`}
                      />
                    </div>
                  ) : (
                    <div className={`flex-1 py-2 px-1 text-base md:text-sm leading-relaxed break-words whitespace-pre-wrap ${
                      rejected ? "line-through text-rose-300/80" :
                      done ? "line-through text-[#647087]" :
                      inProgress ? "text-sky-200" : "text-white"
                    }`}>
                      {it.label}
                    </div>
                  )}
                  {/* Wet-hands quick photo — view mode only. In edit mode
                      the same action lives in the action bar below so the
                      header stays clean.
                      Iter 418.t — swapped from single label+file input to
                      PhotoPickerButton so Android users get the "Take
                      photo / Choose from gallery" menu (was gallery-only). */}
                  {!isEditing && (
                    <PhotoPickerButton
                      onFiles={(files) => attachTaskPhotos(it, files)}
                      className="shrink-0 min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md text-[#a0aabf] hover:text-[#1FB6C1] active:bg-white/5 cursor-pointer"
                      title="Attach photo(s) to this task"
                      ariaLabel="Attach photo to task"
                      testId={`task-photo-${it.id || i}`}
                    >
                      <CameraIcon className="w-5 h-5" />
                    </PhotoPickerButton>
                  )}
                  {/* Edit / Done — single tap to unlock or commit. */}
                  <button
                    onClick={() => setEditingTaskId(isEditing ? null : (it.id || `idx-${i}`))}
                    className={`shrink-0 min-h-[44px] min-w-[44px] px-2 inline-flex items-center justify-center gap-1.5 rounded-md text-sm font-medium ${
                      isEditing
                        ? "bg-[#1FB6C1]/15 text-[#1FB6C1] border border-[#1FB6C1]/40"
                        : "text-[#a0aabf] hover:text-[#1FB6C1] active:bg-white/5 border border-white/10"
                    }`}
                    data-testid={`task-edit-toggle-${it.id || i}`}
                    title={isEditing ? "Done editing" : "Edit this to-do"}
                    aria-label={isEditing ? "Done editing" : "Edit this to-do"}
                  >
                    {isEditing ? (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Done</span>
                      </>
                    ) : (
                      <Pencil className="w-4 h-4" />
                    )}
                  </button>
                </div>

                {/* Status pills — full-width segmented control on mobile.
                    [Iter 351 — 2026-02-26] Dirk: "The addition of the
                    member selector now destroys the task progress. Put
                    members below progress rather than next to it." So
                    the pills own the full row; the assignee picker
                    drops onto its own line beneath. */}
                <div className="mt-3">
                  {techCanTick ? (
                    <TaskStatusSelector
                      value={it.status || "pending"}
                      onChange={(next) => setTaskStatus(it, next)}
                      testIdPrefix={`checklist-status-${i}`}
                    />
                  ) : (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] border ${
                        rejected ? "bg-rose-500/10 border-rose-400/40 text-rose-300" :
                        done ? "bg-emerald-500/10 border-emerald-400/40 text-emerald-300" :
                        inProgress ? "bg-sky-500/10 border-sky-400/40 text-sky-300" :
                        "bg-amber-500/10 border-amber-400/40 text-amber-300"
                      }`}
                      data-testid={`checklist-status-readonly-${i}`}
                    >
                      {rejected ? <span>✕</span> : done ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
                      {rejected ? "Rejected" : done ? "Complete" : inProgress ? "In progress" : "Pending"}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex justify-end">
                  <TaskAssigneePicker
                    testId={`task-assignee-${it.id || i}`}
                    selected={it.assignee_ids || []}
                    selectedNames={it.assignee_names || []}
                    teamUserIds={[
                      ...(job.technician_id ? [job.technician_id] : []),
                      ...(job.staff_ids || []),
                    ]}
                    // Iter 392c — Only show users actually assigned to
                    // this Job Card (primary tech + staff_ids). Anyone
                    // else who wants to be a task assignee must first
                    // be added to the JC's team. Prevents accidentally
                    // handing tasks to techs on other sites.
                    extraUsers={[]}
                    users={users}
                    canEdit={true}
                    appointedBy={
                      it.assignee_edit_by_name
                        ? {
                            name: it.assignee_edit_by_name,
                            at: it.assignee_edit_at,
                          }
                        : null
                    }
                    onChange={async (nextIds) => {
                      // Optimistic local update so the pill flips
                      // instantly. The server response overwrites the
                      // whole checklist so any drift is corrected on
                      // the next save.
                      setJob((j) => {
                        if (!j) return j;
                        const nextList = (j.checklist || []).map((row) =>
                          row.id === it.id
                            ? { ...row, assignee_ids: nextIds }
                            : row,
                        );
                        return { ...j, checklist: nextList };
                      });
                      try {
                        const { data } = await api.patch(
                          `/jobcards/${id}/checklist/${it.id}`,
                          { assignee_ids: nextIds },
                        );
                        setJob(data);
                      } catch (e) {
                        toast.error(
                          e?.response?.data?.detail ||
                            "Could not update the assignee.",
                        );
                      }
                    }}
                  />
                </div>

                {/* Edit-mode action bar — only revealed when the row is
                    in edit mode. Big 44px tap targets, labelled where
                    space allows. Keeps the header row clean. */}
                {isEditing && (
                  <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-1">
                    <PhotoPickerButton
                      onFiles={(files) => attachTaskPhotos(it, files)}
                      className="flex-1 min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-md text-[#a0aabf] hover:text-[#1FB6C1] active:bg-white/5 cursor-pointer text-xs"
                      title="Attach photo(s) to this task"
                      testId={`task-photo-edit-${it.id || i}`}
                    >
                      <CameraIcon className="w-4 h-4" />
                      <span>Photo</span>
                    </PhotoPickerButton>
                    {isManager && (
                      <>
                        <button
                          type="button"
                          onClick={() => moveItem(i, -1)}
                          disabled={i === 0}
                          className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md text-[#a0aabf] hover:text-[#1FB6C1] active:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move task up"
                          data-testid={`task-move-up-${it.id || i}`}
                          aria-label="Move task up"
                        ><ArrowUp className="w-4 h-4" /></button>
                        <button
                          type="button"
                          onClick={() => moveItem(i, 1)}
                          disabled={i === (job.checklist || []).length - 1}
                          className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md text-[#a0aabf] hover:text-[#1FB6C1] active:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move task down"
                          data-testid={`task-move-down-${it.id || i}`}
                          aria-label="Move task down"
                        ><ArrowDown className="w-4 h-4" /></button>
                        <button
                          onClick={() => removeItem(i)}
                          className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md text-[#a0aabf] hover:text-rose-400 active:bg-rose-500/10"
                          data-testid={`checklist-delete-${i}`}
                          title="Delete task"
                          aria-label="Delete task"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Item photos — flush left on mobile so we don't waste
                    horizontal space; indented only on tablet+ to mirror
                    the comment thread. */}
                {(it.photos || []).length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3 sm:ml-9">
                    {(() => {
                      const urls = it.photos.map((src) =>
                        (typeof src === "string" && (src.startsWith("http") || src.startsWith("data:")))
                          ? src
                          : (typeof src === "string" ? uploadUrl(src) : src)
                      );
                      return urls.map((url, pi) => (
                        <SafeTaskPhotoTile
                          key={pi}
                          url={url}
                          taskIdx={i}
                          photoIdx={pi}
                          onView={() => setLightbox({ urls, index: pi })}
                          onRetake={(newUrlOrId) => replaceItemPhoto(i, pi, newUrlOrId)}
                        />
                      ));
                    })()}
                  </div>
                )}

                {/* Item comments — Wave 26.3: long-form readable on
                    mobile via ExpandableText, plus a ReadAloud button
                    so techs can listen hands-free. */}
                {(it.comments || []).length > 0 && (
                  <div className="mt-3 sm:ml-9 space-y-2">
                    {it.comments.map((c) => {
                      const hidden = c.visible_to_customer === false;
                      return (
                        <div
                          key={c.id}
                          className={`text-sm sm:text-xs bg-white/5 border rounded-md p-2.5 sm:p-2 ${
                            hidden ? "border-amber-400/30 bg-amber-500/[0.05]" : "border-white/5"
                          }`}
                          data-testid={`task-comment-row-${c.id}`}
                        >
                          <div className="text-[#647087] mb-1 flex items-center justify-between gap-2 text-xs">
                            <span>
                              <span className="text-[#1fb6c1]">{c.by_user_name}</span> ·{" "}
                              {new Date(c.created_at).toLocaleString('en-GB')}
                              {/* Iter 363 — eye toggle: click to hide the
                                  comment from the customer portal + PDF.
                                  Amber "Hidden" pill signals the state
                                  clearly for tech + HR review. */}
                              <button
                                type="button"
                                onClick={() => toggleCommentVisibility(it, c)}
                                title={hidden
                                  ? "Hidden from customer portal + PDF · click to show"
                                  : "Visible to customer · click to hide"}
                                className={`ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wider transition ${
                                  hidden
                                    ? "border-amber-400/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
                                    : "border-white/10 bg-white/[0.03] text-[#8892a6] hover:text-white hover:border-white/20"
                                }`}
                                data-testid={`task-comment-visibility-${c.id}`}
                              >
                                {hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                {hidden ? "Hidden" : "Shown"}
                              </button>
                            </span>
                            <ReadAloudButton text={c.text} size="xs" testId={`task-comment-tts-${c.id}`} />
                          </div>
                          <ExpandableText
                            text={c.text}
                            lines={3}
                            textClassName="text-[#dde3ec] whitespace-pre-wrap text-sm sm:text-xs leading-relaxed"
                            testId={`task-comment-${c.id}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* legacy single-comment */}
                {it.comment && (it.comments || []).length === 0 && (
                  <div className="mt-2 sm:ml-9 text-sm sm:text-xs text-[#a0aabf] flex items-start gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <ExpandableText
                      text={it.comment}
                      lines={3}
                      textClassName="text-[#a0aabf] whitespace-pre-wrap text-sm sm:text-xs"
                      className="flex-1"
                      testId={`task-legacy-comment-${it.id}`}
                    >
                      <ReadAloudButton text={it.comment} size="xs" testId={`task-legacy-comment-tts-${it.id}`} />
                    </ExpandableText>
                  </div>
                )}

                {/* Compose — Wave 27.1: stacked layout on mobile so the
                    textarea gets full width and the action buttons land
                    on a dedicated row with proper 44px tap targets.
                    Status pills above remain always-clickable. */}
                {isEditing && (
                <div className="mt-3 sm:ml-9 space-y-2">
                  <VoiceTextarea
                    key={`comment-${it.id || `idx-${i}`}`}
                    value={commentDrafts[String(it.id || `idx-${i}`)] || ""}
                    onChange={(next) => {
                      const k = String(it.id || `idx-${i}`);
                      setCommentDrafts((prev) => ({ ...prev, [k]: next }));
                    }}
                    onCommit={({ refined }) => {
                      const v = (refined || "").trim();
                      if (!v) return;
                      const k = String(it.id || `idx-${i}`);
                      setCommentDrafts((prev) => ({ ...prev, [k]: v }));
                    }}
                    placeholder={(it.comments || []).length > 0
                      ? "Add another comment…"
                      : "Add a comment for this task…"}
                    rows={3}
                    context="jc_task_comment"
                    className="hv-input w-full py-2 px-3 text-base md:text-sm resize-y min-h-[88px]"
                    data-testid={`item-comment-input-${it.id || i}`}
                    testIdPrefix={`item-comment-${it.id || i}`}
                    onKeyDown={(e) => {
                      // Bug 5 — Dirk 2026-05-13. Plain Enter creates a new
                      // line so the tech can paste bullet lists. Only the
                      // explicit Ctrl/Cmd+Enter combo submits.
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        addItemComment(it);
                      }
                    }}
                  />
                  <div className="flex items-stretch gap-2">
                    <button
                      onClick={() => addItemComment(it)}
                      disabled={!!commentBusy[String(it.id || "")]}
                      className="hv-btn hv-btn-primary flex-1 min-h-[44px] justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`item-comment-send-${it.id || i}`}
                      title="Add comment"
                      aria-label="Add comment"
                    >
                      <Plus className="w-4 h-4" /> {commentBusy[String(it.id || "")] ? "Sending…" : "Add comment"}
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        addItemPhoto(it, e.target.files);
                        e.target.value = "";
                      }}
                      className="hidden"
                      ref={(el) => (itemFileRef.current[it.id] = el)}
                      data-testid={`item-photo-input-${it.id || i}`}
                    />
                    {/* Iter 418.t — comment photo picker (Android camera+gallery choice). */}
                    <PhotoPickerButton
                      onFiles={(files) => addItemPhoto(it, files)}
                      className="hv-btn hv-btn-ghost min-h-[44px] min-w-[44px] justify-center shrink-0"
                      testId={`item-photo-btn-${it.id || i}`}
                      title="Attach photo to this task"
                      ariaLabel="Attach photo to this task"
                    >
                      <Camera className="w-4 h-4" />
                    </PhotoPickerButton>
                  </div>
                  {/* [Iter 341] Full-width progress pill — WhatsApp-style.
                      Replaces the tiny 16 px corner badge which the tech
                      couldn't see on mobile. */}
                  <TaskUploadBadge jobId={id} taskId={it.id} idx={i} />
                </div>
                )}
              </div>
            );
          })}

          {/* Iter 349.f (2026-02-25) — Tech-added ad-hoc task/comment
              entry point (BOTTOM). Sits below the last task. */}
          {!isCreate && job.id && (
            <AddTechNoteButton
              jobId={job.id}
              variant="bottom"
              onCreated={handleTechNoteCreated}
            />
          )}

          {isManager && (
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <VoiceTextarea
                  value={newItem}
                  onChange={(v) => setNewItem(v)}
                  placeholder="Add a checklist item… (Enter = new line · Ctrl/⌘+Enter to add)"
                  rows={2}
                  context="task_title"
                  className="hv-input w-full resize-y min-h-[48px]"
                  data-testid="checklist-new-input"
                  testIdPrefix="checklist-new"
                  onKeyDown={(e) => {
                    // Bug 5 — Dirk 2026-05-13. Plain Enter = new line so
                    // techs can type bullet/multi-line task titles. Submit
                    // is Ctrl/Cmd+Enter only.
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      addItem();
                    }
                  }}
                />
              </div>
              <button onClick={addItem} className="hv-btn hv-btn-primary shrink-0" data-testid="checklist-add-btn">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
          )}
          {!isManager && (job.checklist || []).length === 0 && (
            <div className="text-sm text-[#647087]">Manager hasn't added any checklist items yet.</div>
          )}
        </div>
      </div>
      )}

      {/* Before / After block REMOVED per user request (2026-02-19) — the
          dedicated proof-pair block was duplicated work; technicians now
          upload all photos directly into the Photos block on the JC,
          and the customer portal renders the gallery in the same order
          without needing a separate "before/after" pairing. */}

      {(isCreate || tab === "overview") && (<>
      {/* Service rating — internal metrics, three perspectives */}
      <ServiceRatingBlock job={job} canEditAll={canEditMeta} canEditTech={canEditMeta || user?.role === "technician"} onChange={update} />

      {/* Signoffs — order matches the Service rating above: Customer · Technician · Management */}
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { type: "client", title: "Client sign-off", field: "client_signoff", visibleToTech: true },
          { type: "tech", title: "Technician sign-off", field: "tech_signoff", visibleToTech: true },
          { type: "mgmt", title: "Management sign-off", field: "mgmt_signoff", visibleToTech: false },
        ].filter((s) => {
          // Hide the management signoff card from technicians entirely —
          // backend already drops mgmt_signoff updates from tech, but we
          // shouldn't even show the option (it's role-confusing).
          if (user?.role === "technician" && !s.visibleToTech) return false;
          return true;
        }).map((s) => {
          const signed = job[s.field];
          return (
            <div key={s.type} className="hv-card p-4" data-testid={`signoff-${s.type}`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-display font-bold">{s.title}</h4>
                {signed ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                ) : (
                  <ImageIcon className="w-4 h-4 text-[#647087]" />
                )}
              </div>
              {signed ? (
                <div>
                  <img
                    src={signed.signature}
                    alt=""
                    className="w-full h-32 object-contain bg-white border border-white/10 rounded-md"
                  />
                  <div className="text-xs text-[#a0aabf] mt-2">
                    By <span className="text-white">{signed.name}</span> ·{" "}
                    {new Date(signed.signed_at).toLocaleString('en-GB')}
                  </div>
                  {isManager && (
                    <button
                      onClick={() => update({ [s.field]: null })}
                      className="hv-btn hv-btn-ghost mt-2 w-full text-xs"
                    >
                      <RotateCcw className="w-3 h-3" /> Reset
                    </button>
                  )}
                </div>
              ) : (
                <SignaturePad
                  onSave={(d, name) => signoff(s.type, d, name)}
                  label="Sign here"
                  testIdPrefix={`signoff-${s.type}-pad`}
                  /* [Iter 331] Client sign-off must capture the typed name
                     (someone may sign on the customer's behalf — we need
                     to know who). Tech / mgmt default to the logged-in
                     user's name on the backend, so name entry is optional
                     there — we still show the field for auditability. */
                  nameField={s.type === "client"}
                  nameLabel={
                    s.type === "client"
                      ? "Print name (person signing on behalf of the customer)"
                      : "Print name"
                  }
                  defaultName={
                    s.type === "client"
                      ? (customer?.name || "")
                      : (user?.name || "")
                  }
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Iter 424.e — Job Report. Below signatures (per user brief).
          Free-text narrative of what happened on site; visible to the
          customer in the portal and in the PDF (before tasks). */}
      {!isCreate && (
        <JobReportBlock
          jobcardId={id}
          value={job.job_report || ""}
          canEdit={isManager || user?.role === "technician"}
          onChanged={load}
        />
      )}
      </>)}

      {/* Wave 25.14 — Documents (instruction manuals, datasheets) + Job
          photos moved BELOW signatures per user request: docs/photos belong
          at the bottom of the JC, signatures should be visually adjacent to
          the rating + tasks they certify. */}
      {(isCreate || tab === "documents") && !isCreate && (
        <JCDocumentsBlock
          jobcardId={id}
          documents={job.documents || []}
          onChanged={load}
        />
      )}

      {(isCreate || tab === "photos") && (
      <div className="hv-card p-5">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-display text-xl font-bold">Job photos</h3>
          {photoUploads.kind === "job" && photoUploads.total > 0 && (
            <div
              className="flex-1 min-w-[160px] max-w-[260px]"
              data-testid="photo-upload-progress"
            >
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-[#1fb6c1] transition-all"
                  style={{ width: `${Math.round((photoUploads.done / photoUploads.total) * 100)}%` }}
                />
              </div>
              <div className="text-[10px] text-[#1fb6c1] font-mono mt-0.5 text-right">
                Uploading {photoUploads.done} / {photoUploads.total}…
              </div>
            </div>
          )}
          <PhotoPickerButton
            onFiles={(files) => handleJobPhoto({ target: { files } })}
            className="hv-btn hv-btn-primary"
            testId="photo-upload-btn"
            title="Add job card photos — take with camera or choose from gallery"
          >
            <Camera className="w-4 h-4" /> Upload
          </PhotoPickerButton>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(() => {
            // [Iter 342 — 2026-02-25] Merge task-inline photos into the
            // "Job photos" grid so the tab count matches "every photo
            // linked to this JC". Dirk 2026-02-25: "the photos section
            // must show all photos linked to this job card. If you
            // upload a photo to a task inline, it means it is linked
            // to a specific task, and it must still show in the total
            // job card photos as well."
            //
            // We dedupe by upload_id so a photo attached to BOTH the
            // main gallery and a task (via drag/drop or copy) only
            // renders once — with the task badge if present.
            //
            // PDF is unaffected — the PDF template still renders task
            // photos inline only and main-gallery photos in the
            // bottom grid, so a JC PDF has ZERO duplication.
            const raw = job.photos || [];
            const seen = new Set();
            const merged = [];
            raw.forEach((src) => {
              const id = typeof src === "string"
                ? (src.startsWith("http") || src.startsWith("data:") ? src : src)
                : (src?.upload_id || src?.id || null);
              if (id && seen.has(id)) return;
              if (id) seen.add(id);
              merged.push({ src, taskIdx: null });
            });
            (job.checklist || []).forEach((task, ti) => {
              (task.photos || []).forEach((src) => {
                const id = typeof src === "string"
                  ? (src.startsWith("http") || src.startsWith("data:") ? src : src)
                  : (src?.upload_id || src?.id || null);
                if (id && seen.has(id)) return;
                if (id) seen.add(id);
                // task numbering is 1-indexed in the UI (matches PDF).
                merged.push({ src, taskIdx: ti + 1 });
              });
            });

            const photoUrls = merged.map(({ src }) =>
              typeof src === "string" && (src.startsWith("http") || src.startsWith("data:"))
                ? src
                : typeof src === "string"
                ? uploadUrl(src)
                : src
            );
            // Extract upload_id when the entry is a bare id or dict — drives
            // the Hidro scan badge (Dirk 2026-02 — "ALL uploads, even photos
            // scanned by Hidro for descriptions").
            const photoIds = merged.map(({ src }) => {
              if (typeof src === "string") {
                if (src.startsWith("http") || src.startsWith("data:")) return null;
                return src;
              }
              return src?.upload_id || src?.id || null;
            });
            return photoUrls.map((url, i) => (
              <div key={i} className="relative group rounded-md overflow-hidden border border-white/10">
                <button onClick={() => setLightbox({ urls: photoUrls, index: i })} className="block w-full">
                  <img src={url} alt="" className="w-full h-32 object-cover" />
                </button>
                {merged[i].taskIdx && (
                  <div
                    className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-emerald-500/85 text-[10px] font-mono font-bold text-black pointer-events-none"
                    data-testid={`photo-task-badge-${i}`}
                    title={`Attached to Task ${merged[i].taskIdx}`}
                  >
                    T{merged[i].taskIdx}
                  </div>
                )}
                {photoIds[i] && (
                  <div className="absolute top-1 left-1 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
                    <UploadScanBadge uploadId={photoIds[i]} compact />
                  </div>
                )}
                {isManager && merged[i].taskIdx === null && (
                  <button
                    onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 bg-black/70 rounded p-1 text-rose-300 opacity-0 group-hover:opacity-100 transition"
                    data-testid={`photo-delete-${i}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            ));
          })()}
          {(!job.photos || job.photos.length === 0) && !(job.checklist || []).some((t) => (t.photos || []).length > 0) && (
            <div className="col-span-full text-sm text-[#647087]">No photos uploaded.</div>
          )}
        </div>
      </div>
      )}

      {/* Iter 418.e (Dirk, 2026-02-28) — Removed the `tab === "photos"`
          gate: task-inline photo clicks fire setLightbox from the Tasks
          tab, but the lightbox never rendered because it was scoped to
          the Photos tab only. Now it lives at page-level so any thumb
          (main gallery, task-inline, notes-inline, etc.) opens the
          same viewer with prev/next/close working. */}
      {lightbox && (() => {
        const urls = lightbox.urls || [];
        const cur = urls[lightbox.index] || "";
        const m = String(cur).match(/\/uploads\/([\w-]+)/);
        const uid = m?.[1];
        return (
          <MediaLightbox
            items={urls.map((u) => ({ src: u }))}
            index={lightbox.index}
            onChange={(i) => setLightbox({ ...lightbox, index: i })}
            onClose={() => setLightbox(null)}
            topBar={uid ? (
              <ShareButton
                kind="upload"
                refId={uid}
                title={`Job card photo`}
                downloadUrl={cur}
                testId="jc-lightbox-share"
              />
            ) : null}
          />
        );
      })()}

      {/* Wave 19 #85 — embedded chat for this JC. Auto-creates the
          thread on first open. */}
      {(isCreate || tab === "chat") && !isCreate && job?.id && (
        <div className="mt-6">
          <EntityChatPanel kind="jobcard" entityId={job.id} />
        </div>
      )}

      {/* Phase 7 Sub-iter 7A — Activity tab via shared HistoryBlock. */}
      {!isCreate && tab === "history" && (
        <HistoryBlock entity={{ id: job.id, kind: "jobcard" }} />
      )}

      {/* [Iter 340 — 2026-02-25] Rejection reason prompt.
          A checklist task can't be marked Rejected without an
          explanatory comment — this modal enforces that. Bailing
          via Cancel or Esc leaves the task's previous status
          intact. */}
      {rejectPromptFor && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
          onClick={cancelRejectPrompt}
          data-testid="reject-reason-backdrop"
        >
          <div
            className="w-full max-w-md rounded-lg border border-rose-500/40 bg-[#0f1218] shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="reject-reason-modal"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-md bg-rose-500/15 border border-rose-500/40 flex items-center justify-center shrink-0">
                <span className="text-rose-300 text-lg">✕</span>
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-lg font-bold text-rose-100">Reject this task?</h3>
                <p className="text-xs text-[#a0aabf] mt-0.5 break-words">
                  <span className="text-white">{rejectPromptFor.taskLabel}</span>
                </p>
              </div>
            </div>
            <label className="block text-[10px] uppercase tracking-wider text-[#a0aabf] font-bold mb-1">
              Reason (required)
            </label>
            <DictTextarea
              autoFocus
              rows={4}
              value={rejectReasonDraft}
              onChange={(e) => setRejectReasonDraft(e.target.value)}
              placeholder="e.g. Wrong part supplied, unable to complete — awaiting correct fitting from supplier."
              className="w-full hv-input text-sm leading-relaxed resize-y"
              data-testid="reject-reason-input"
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={cancelRejectPrompt}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="reject-reason-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitRejectReason}
                disabled={!rejectReasonDraft.trim() || rejectBusy}
                className="hv-btn text-xs bg-rose-500/25 hover:bg-rose-500/35 text-rose-100 border border-rose-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="reject-reason-submit"
              >
                {rejectBusy ? "Rejecting…" : "Reject task"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </EntityShell>
  );
}

/**
 * Iter 341 — 2026-02-25 · Per-task upload progress pill.
 * Replaces the previous tiny corner badge (which was too small to
 * notice on mobile PWA — Dirk 2026-02-25: "you've got no idea if
 * it's actually uploading until it suddenly appears").
 *
 * Renders a full-width WhatsApp-style status pill BELOW the task
 * card body while any photo upload for this task is in flight.
 * Aggregates every queued/uploading/attaching item into one row
 * showing "Uploading 1 of 3 · 47%" with a live progress bar.
 *
 * Subscribes to the global upload manager via `useUploads()` so it
 * survives JC unmount/remount and picks up resumed IDB items on boot.
 */
/* ─────────────── JobReportBlock (Iter 424.e) ──────────────────────
 * Free-text narrative shown below signatures in CRM, on top of the
 * customer portal JC page, and in the PDF (before tasks).
 *
 * Input: <textarea> preserves newlines and bullets typed by the user
 * (·, •, -, *, 1.). Ctrl/⌘+Enter to save; plain Enter = new line.
 * A tiny "Bullet" button prepends "• " to the current line.
 */
function JobReportBlock({ jobcardId, value, canEdit, onChanged }) {
  const [text, setText] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => { setText(value || ""); setDirty(false); }, [value]);

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await api.put(`/jobcards/${jobcardId}`, { job_report: text });
      toast.success("Job report saved.");
      setDirty(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const insertBullet = () => {
    const ta = textareaRef.current;
    if (!ta) { setText((t) => (t ? `${t}\n• ` : "• ")); setDirty(true); return; }
    const start = ta.selectionStart ?? text.length;
    const before = text.slice(0, start);
    const after = text.slice(start);
    // Prepend a bullet at the beginning of the current line.
    const lineStart = before.lastIndexOf("\n") + 1;
    const newText = text.slice(0, lineStart) + "• " + text.slice(lineStart);
    setText(newText);
    setDirty(true);
    // Restore focus + move caret past the bullet.
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + 2;
      ta.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="hv-card p-5" data-testid="jc-job-report">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-bold">Job Report</h3>
          <p className="text-xs text-[#647087] mt-0.5">
            What happened on site. Visible to the customer in the portal and on the PDF.
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={insertBullet}
              className="hv-btn hv-btn-ghost text-xs"
              title="Prepend a bullet to the current line"
              data-testid="jc-job-report-bullet-btn"
            >
              • Bullet
            </button>
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
              data-testid="jc-job-report-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Save
            </button>
          </div>
        )}
      </div>
      {canEdit ? (
        <DictTextarea
          ref={textareaRef}
          value={text}
          onChange={(e) => { setText(e.target.value); setDirty(true); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
              e.preventDefault(); save();
            }
          }}
          placeholder="Write a report of what happened on site, findings, and feedback for the customer. Enter = new line · Ctrl/⌘+Enter = save."
          rows={8}
          className="hv-input w-full resize-y font-mono text-sm whitespace-pre-wrap"
          data-testid="jc-job-report-textarea"
        />
      ) : (
        <div
          className="text-sm text-[#cbd5e1] whitespace-pre-wrap"
          data-testid="jc-job-report-display"
        >
          {text || <span className="text-[#647087] italic">No job report yet.</span>}
        </div>
      )}
    </div>
  );
}


function TaskUploadBadge({ jobId, taskId, idx }) {
  const targetId = `${jobId}:${taskId}`;
  const items = useUploads("jobcard_task", targetId);
  if (items.length === 0) return null;

  const inFlight = items.filter((i) => i.status === "uploading" || i.status === "queued" || i.status === "attaching");
  const failed = items.filter((i) => i.status === "error" || i.status === "attach_failed");
  const done = items.filter((i) => i.status === "done").length;
  const total = items.length;

  if (failed.length > 0 && inFlight.length === 0) {
    return (
      <div
        className="mt-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-1.5 flex items-center justify-between gap-2"
        data-testid={`item-photo-failed-${idx}`}
      >
        <span className="text-[11px] text-rose-200 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
          {failed.length} upload{failed.length === 1 ? "" : "s"} failed
        </span>
        <span className="text-[10px] text-rose-300/80">Open uploads dock to retry</span>
      </div>
    );
  }

  if (inFlight.length === 0) return null;

  // Average % across in-flight items so the bar advances smoothly
  // when 3 files are uploading in parallel.
  const avgPct = Math.round(
    inFlight.reduce((s, it) => s + (Number(it.progress) || 0), 0) / inFlight.length,
  );
  const currentIndex = done + 1;

  return (
    <div
      className="mt-2 rounded-md border border-sky-500/40 bg-sky-500/10 px-2.5 py-1.5"
      data-testid={`item-photo-progress-${idx}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[11px] text-sky-100 flex items-center gap-1.5 min-w-0">
          <span className="w-1.5 h-1.5 rounded-full bg-sky-300 animate-pulse shrink-0" />
          <span className="truncate">
            Uploading {currentIndex} of {total}
            {inFlight[0]?.filename ? ` · ${(inFlight[0].filename).slice(0, 28)}` : ""}
          </span>
        </span>
        <span className="text-[10px] font-mono tabular-nums text-sky-200 shrink-0">
          {avgPct}%
        </span>
      </div>
      <div className="h-1 rounded-full overflow-hidden bg-sky-950/60">
        <div
          className="h-full bg-sky-400 transition-[width] duration-200"
          style={{ width: `${Math.max(3, avgPct)}%` }}
        />
      </div>
    </div>
  );
}

/**
 * <SafeTaskPhotoTile /> — Iter 362 (2026-02-27)
 *
 * Dirk: "we can never have broken images because that's the only form
 * of proof we have."
 *
 * Renders a JC task photo tile with foolproof error handling:
 *   1. Attempts to load the image.
 *   2. If the `<img onError>` fires (bytes 404 / gone), swaps to a
 *      "Photo missing — Retake" placeholder with a big camera button.
 *   3. Retake opens the phone camera; the fresh capture is uploaded
 *      and swapped in via `onRetake`.
 *
 * Backend Iter 362 post-write HEAD verify prevents NEW broken uploads
 * from being created — this tile handles legacy broken rows.
 */
function SafeTaskPhotoTile({ url, taskIdx, photoIdx, onView, onRetake }) {
  const [broken, setBroken] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !onRetake) return;
    setBusy(true);
    try { await onRetake(f); } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  if (broken) {
    return (
      <div
        className="relative rounded-md overflow-hidden border border-rose-500/40 bg-rose-500/10 flex flex-col items-center justify-center h-20 sm:h-16 gap-1 px-1.5"
        data-testid={`task-photo-broken-${taskIdx}-${photoIdx}`}
      >
        <span className="text-[10px] uppercase tracking-wider text-rose-200 leading-tight text-center">
          Photo<br />missing
        </span>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="text-[10px] font-semibold text-rose-100 border border-rose-300/60 hover:bg-rose-400/20 px-2 py-0.5 rounded"
          data-testid={`task-photo-retake-${taskIdx}-${photoIdx}`}
        >
          {busy ? "…" : "Retake"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onPickFile}
          className="hidden"
        />
      </div>
    );
  }
  return (
    <button
      onClick={onView}
      className="relative rounded-md overflow-hidden border border-white/10 hover:border-[#1fb6c1]/60"
      data-testid={`task-photo-${taskIdx}-${photoIdx}`}
    >
      <img
        src={url}
        alt=""
        className="w-full h-20 sm:h-16 object-cover"
        onError={() => setBroken(true)}
      />
    </button>
  );
}


